import { API_BASE_URL } from '../config/api';

export interface Customer {
    customer_id: number;
    customer_name: string;
    poc_name?: string;
    poc_contact?: string;
    emirate?: string;
    office_tel?: string;
    email_address?: string;
    trn?: string;
    address?: string;
    payment_terms?: string;
    credit_limit?: number;
}

export interface EmailPdfRequest {
    to: string;
    cc?: string;
    subject?: string;
    body?: string;
}

export interface EmailPdfResponse {
    ok?: boolean;
    message?: string;
    to?: string[];
    cc?: string[];
    sender?: string;
    attachment_name?: string;
    security?: string;
    host?: string;
    port?: number;
}

export interface EstimateShareLinkRequest {
    regenerate?: boolean;
    channel?: string;
}

export interface EstimateShareLinkResponse {
    share_url: string;
    expires_at: string;
    token: string;
    attachment_name: string;
    is_new: boolean;
}

export interface SMTPEmailConfig {
    host: string;
    port: number;
    security: string;
    username: string;
    password: string;
    sender_name: string;
    configured: boolean;
}

export interface PagedResult<T> {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
    next_offset?: number | null;
    items: T[];
}

export interface Material {
    id: number;
    name: string;
    description: string;
    unit_id?: number;
    unit_rate: number;
}

export interface InquirySummary {
    inquiry_id: number;
    customer_id: number;
    customer_name?: string;
    customer_tel?: string;
    customer_address?: string;
    project_scope?: string;
    project_description?: string;
    attention?: string;
    enquiry_ref?: string;
    project_type_id: number;
    date_enquired?: string;
    inquiry_status_id: number;
    followup_date?: string;
    remarks?: string;
    segment?: string;
    installation_in_scope?: boolean;

    // Enriched Fields
    inquiry_number?: string;
    project_type_name?: string;
    status_name?: string;
}

export interface InquiryDetail extends InquirySummary {
    linked_estimate_id?: number;
}

export interface AdminControlsUpdate {
    pm_design_cost?: number;
    pm_project_manager_cost?: number;
    pm_site_intervention_cost?: number;
    pm_documentation_cost?: number;
    pm_travel_cost?: number;

    skilled_manhours_perc?: number;
    unskilled_manhours_perc?: number;
    non_working_hours_per_week?: number;

    logistics_packing_loading_perc?: number;
    logistics_freight_cost_perc?: number;

    admin_contigency_perc?: number;
    admin_warranty_perc?: number;
    admin_retention_perc?: number;
    admin_banking_finance_perc?: number;
    admin_other_cost_perc?: number;

    oh_per_sqm?: number;
    profit_margin?: number;
    discount_percent?: number;
    vat_percent?: number;

    attention?: string;
    customer_address?: string;
    enquiry_ref?: string;
    validity?: string;
    annexure_a?: string;
    annexure_b?: string;
}

export interface EstimateBOM {
    estimate_bom_id: number;
    bom_material_id?: number;
    material_id?: number;
    quantity: number;
    unit_rate: number;
    total_cost: number;
    material_name?: string;
    supplier_id?: number;
    supplier_name?: string;
    default_cost_source?: string;
}

export interface EstimatePart {
    estimate_part_id: number;
    part_name: string;
    quotation_description?: string;
    quantity: number;
    dimension?: string;
    area_m2: number;
    thickness?: number;
    weight_kgs: number;
    man_hours_required?: number;
    mould_area: number;
    mould_cost_m2: number;
    mould_total_cost?: number;
    mould_quantity?: number;
    design_premium_perc?: number;
    design_premium_amount?: number;
    grp_labour_rate_per_m2?: number;
    grc_design_mix_model_id?: number;
    grc_type_id?: number;
    grc_labour_rate_per_kg?: number;
    m2_per_hour_production: number;
    material_type_id?: number;
    material_type_name?: string;
    unit_id?: number;
    unit_name?: string;
    mould_type_id?: number;
    part_ref_code?: string;
    man_hours_per_unit?: number;
    pricing_mode?: 'calculated' | 'manual_quote' | string;
    manual_unit_price?: number;
    unit_label?: string;
    remarks?: string;
    cost_entry_mode?: 'simple' | 'detailed' | string;
    simple_raw_material_cost?: number;
    simple_accessories_cost?: number;
    simple_labour_cost?: number;
    simple_overhead_cost?: number;
    tooling_reviewed?: boolean;
    production_reviewed?: boolean;
    bom_reviewed?: boolean;
    bom_items: EstimateBOM[];
    installation_cost?: number;
    packing_cost?: number;
    freight_cost?: number;
    project_fee?: number;
    admin_cost?: number;

    // Legacy backend breakdown fields
    area_total?: number;
    pattern_mould_cost?: number;
    production_cost?: number;
    labour_cost_calc?: number; // [NEW] Separate Labour
    bom_cost?: number;
    installation_cost_calc?: number;
    packing_loading_cost?: number;
    freight_cost_calc?: number;
    proj_eng_cost?: number;
    admin_cost_calc?: number;
    overhead_cost?: number;
    direct_cost?: number;
    total_cost?: number;
    base_price_per_sqm?: number;
    revised_base_price_per_sqm?: number;
    selling_price_per_unit?: number;
    selling_price_total?: number;
    round_off?: number;

    // Production Breakdown
    grc_material_cost_unit?: number;
    grc_labour_cost_unit?: number;
    grc_total_cost_unit?: number;
    grc_total_cost_all?: number;
    grc_auxiliary_allocation?: number;
    grc_resolved_design_mix_model_id?: number;
    grc_resolved_design_mix_name?: string;
    grc_resolved_grc_type_id?: number;
    grc_resolved_grc_type_name?: string;
    grc_resolved_labour_rate_per_kg?: number;
    grp_material_cost_unit?: number;
    grp_labour_cost_unit?: number;
    grp_total_cost_unit?: number;
    grp_total_cost_all?: number;
    grp_material_cost_total?: number;
    grp_resolved_labour_rate_per_m2?: number;
    grp_misc_allocation?: number;
    grp_auxiliary_allocation?: number;
    grp_production_items?: GRPMaterialRow[];
}

export interface GRPMaterialRow {
    grp_material_id?: number;
    material_id?: number;
    aed_cost: number;
    area_coverage: number;
    application_layer: number;
    material_name?: string;
    unit?: string;
}

export interface DeliveryItem {
    estimate_part_id?: number;
    so_detail_id?: number;
    quantity_shipped: number;
    part_name?: string;
    item_id?: number;
    item_code?: string;
    item_name?: string;
}

export interface DeliveryDispatchLineCheck {
    estimate_part_id?: number;
    so_detail_id?: number;
    line_source?: string;
    part_name?: string;
    item_id?: number;
    item_code?: string;
    item_name?: string;
    required_qty: number;
    current_stock: number;
    available_for_line: number;
    short_by: number;
    stock_ok: boolean;
    reason?: string | null;
}

export interface DeliveryDispatchPrecheck {
    can_dispatch: boolean;
    dispatch_reason: string;
    dispatch_short_lines: number;
    dispatch_checks: DeliveryDispatchLineCheck[];
}

export interface DeliveryCreateRequest {
    so_id: number;
    dn_date: string; // ISO datetime
    created_by: string;
    remarks?: string;
    dn_ref?: string;
    delivery_location?: string;
    po_ref?: string;
    items: DeliveryItem[];
}

export interface DeliveryUpdateRequest {
    dn_date?: string;
    remarks?: string;
    dn_ref?: string;
    delivery_location?: string;
    po_ref?: string;
    created_by?: string;
    items?: DeliveryItem[];
}

export interface DeliveryNoteDetail extends DeliveryNoteSummary {
    items: DeliveryItem[];
}

export interface DeliveryNoteSummary {
    dn_id: number;
    dn_number: string;
    dn_date: string;
    status_id: number;
    status_name?: string;
    so_id: number;
    customer_name?: string;
    created_by?: string;
    remarks?: string;
    dn_ref?: string;
    delivery_location?: string;
    po_ref?: string;
    item_count: number;
    can_create_tax_invoice?: boolean;
    create_tax_invoice_reason?: string;
    existing_tax_invoice_id?: number | null;
    existing_tax_invoice_no?: string | null;
    tax_invoice_mode?: 'create' | 'append' | 'blocked_posted' | 'covered' | 'blocked_status' | 'blocked_schema' | 'blocked_source' | string;
    existing_tax_invoice_status_id?: number | null;
    existing_tax_invoice_status_name?: string | null;
    can_dispatch?: boolean;
    dispatch_reason?: string;
    dispatch_short_lines?: number;
    dispatch_checks?: DeliveryDispatchLineCheck[];
}

export type DocumentCategory = 'LPO' | 'ENQUIRY_CONTENT' | 'ACK_INVOICE' | 'ACK_DN' | 'SPECIFICATION' | 'PRODUCT_IMAGE';

export interface DocumentAttachment {
    doc_id: number;
    entity_type: 'SO' | 'INQUIRY' | 'INVOICE' | 'MATERIAL' | 'FG';
    entity_id: number;
    category: DocumentCategory | string;
    version_no: number;
    is_current: boolean;
    original_file_name: string;
    stored_file_name: string;
    content_type?: string;
    file_ext?: string;
    file_kind?: 'pdf' | 'image' | 'other';
    is_previewable?: boolean;
    preview_url?: string;
    download_url?: string;
    size_bytes: number;
    relative_path: string;
    note_text?: string | null;
    uploaded_by?: string;
    uploaded_at?: string;
    deleted_at?: string | null;
    deleted_by?: string | null;
}

export interface DocumentAttachmentListResponse {
    documents: DocumentAttachment[];
}

export interface CalculateRequest {
    header: {
        customer_id?: number;
        customer_name?: string;
        customer_name_override?: string;
        project_type_id?: number;
        project_description?: string;
        entry_mode?: 'standard' | 'quick_quote' | string;
        quote_mode?: 'lump_sum' | 'itemized' | string;
        attention?: string;
        customer_address?: string;
        customer_address_override?: string;
        enquiry_ref?: string;
        labour_rate_per_hour: number;
        overhead_per_sqm: number;
        // Alias used by legacy calculation endpoint.
        oh_per_sqm?: number;
        profit_margin: number;
        discount_percent: number;
        vat_percent: number;

        // Legacy header parameters (fractions in Access FE, e.g. 0.01 = 1%)
        tooling_consumable_perc?: number;
        tooling_jigs_fixtures_perc?: number;
        tooling_misc_perc?: number;
        logistics_freight_cost_perc?: number;
        logistics_packing_loading_perc?: number;
        admin_contigency_perc?: number;
        admin_warranty_perc?: number;
        admin_retention_perc?: number;
        admin_banking_finance_perc?: number;
        admin_other_cost_perc?: number;
        prod_aed_per_m2_grp?: number;
        grp_misc_consumables_perc?: number;
        prod_aed_per_kg?: number;
        prod_aed_per_kg_grg?: number;
        apply_auxiliary_to_grc?: boolean;
        apply_auxiliary_to_grp?: boolean;
        direct_wastage_perc?: number;
        direct_painting_labour_perc?: number;
        direct_electricity_perc?: number;
        direct_acid_wash_labour_perc?: number;
        direct_styro_foam?: number;
        direct_production_accessories?: number;

        // New Manhour Fields
        skilled_manhours_perc?: number;
        unskilled_manhours_perc?: number;
        non_working_manhours_perc?: number;
        non_working_hours_per_week?: number;

        // Project Management Fixed Costs
        pm_design_cost?: number;
        pm_project_manager_cost?: number;
        pm_site_intervention_cost?: number;
        pm_documentation_cost?: number;
        pm_travel_cost?: number;

        // Setup Content
        validity?: string;
        annexure_a?: string;
        annexure_b?: string;
        installation_in_scope?: boolean;
        tooling_reviewed?: boolean;
        production_reviewed?: boolean;
        bom_reviewed?: boolean;
        installation_reviewed?: boolean;
        scope_reviewed?: boolean;
        admin_reviewed?: boolean;
        design_premium_reviewed?: boolean;
    };
    parts: {
        part_name?: string;
        dimension?: string;
        quantity: number;
        area_m2: number;
        thickness?: number;
        weight_kgs: number;
        mould_area: number;
        mould_cost_m2: number;
        m2_per_hour_production: number;
        man_hours_required?: number;
        bom_cost: number;
        material_type_id?: number;
        unit_id?: number;
        mould_type_id?: number;
        pricing_mode?: 'calculated' | 'manual_quote' | string;
        manual_unit_price?: number;
        unit_label?: string;
        quotation_description?: string;
        cost_entry_mode?: 'simple' | 'detailed' | string;
        simple_raw_material_cost?: number;
        simple_accessories_cost?: number;
        simple_labour_cost?: number;
        simple_overhead_cost?: number;

        // Gap Closure
        installation_cost?: number;
        packing_cost?: number;
        freight_cost?: number;
        project_fee?: number;
        admin_cost?: number;
        design_premium_perc?: number;
        grp_labour_rate_per_m2?: number;
        part_ref_code?: string;
        tooling_reviewed?: boolean;
        production_reviewed?: boolean;
        bom_reviewed?: boolean;

        bom_items?: {
            bom_material_id?: number;
            material_id?: number;
            quantity: number;
            unit_rate: number;
        }[];
    }[];

    // Optional legacy lists (used for accurate draft calculations without saving)
    production_materials?: {
        material_name?: string;
        aed_cost: number;
        conversion_ratio: number;
        unit?: string;
    }[];
    grg_production_materials?: {
        material_name?: string;
        aed_cost: number;
        conversion_ratio: number;
        unit?: string;
    }[];
    installation_items?: {
        estimate_inst_id?: number;
        main_category?: string;
        sub_category?: string;
        quantity?: number;
        unit?: string;
        unit_rate?: number;
        man_hours?: number;
        cost: number;
    }[];
}

export interface CalculationResult {
    parts_breakdown: any[];
    summary: {
        total_cost: number;
        estimate_amount: number;
        discount_amount: number;
        amount_after_discount: number;
        vat_amount: number;
        grand_total: number;
    };
}

export interface EstimateProductionMaterial {
    material_name: string;
    aed_cost: number;
    conversion_ratio: number;
    unit: string;
}

export interface EstimateInstallationItem {
    estimate_inst_id: number;
    main_category: string;
    sub_category: string;
    quantity: number;
    unit: string;
    unit_rate: number;
    man_hours: number;
    cost: number;
}

export interface InstallationItemUpdate {
    estimate_inst_id: number;
    quantity?: number;
    man_hours?: number;
    unit_rate?: number;
}

export interface MaterialType {
    material_type_id: number;
    material_type: string;
}

export interface Unit {
    unit_id: number;
    unit: string;
}

export interface MouldType {
    mould_type_id: number;
    mould_type: string;
    mould_cost: number;
}

export interface ProductionSettingsUpdate {
    prod_aed_per_kg?: number;
    prod_aed_per_kg_grg?: number;
    apply_auxiliary_to_grc?: boolean;
    apply_auxiliary_to_grp?: boolean;
    direct_electricity_perc?: number;
    direct_wastage_perc?: number;
    direct_painting_labour_perc?: number;
    direct_acid_wash_labour_perc?: number;
    direct_styro_foam?: number;
    direct_production_accessories?: number;
    prod_aed_per_m2_grp?: number;
    grp_misc_consumables_perc?: number;
    non_working_hours_per_week?: number;
}

export interface GRPMaterialCatalogItem {
    grp_material_id?: number;
    material_id: number;
    material_name: string;
    unit?: string;
    saudi_cost?: number;
    aed_cost: number;
    usd_cost?: number;
    area_coverage: number;
    wac_rate?: number;
    default_cost_source?: string;
    description?: string;
}

export interface BOMCatalogItem {
    bom_material_id?: number;
    material_id?: number;
    material_name?: string;
    unit_rate?: number;
    unit_id?: number;
    unit?: string;
    wac_rate?: number;
    default_cost_source?: string;
}

export interface EstimateGRPMaterialItem {
    grp_production_material_id?: number;
    estimate_part_id?: number;
    grp_material_id?: number;
    material_id?: number;
    material_name?: string;
    unit?: string;
    aed_cost: number;
    area_coverage: number;
    application_layer: number;
}

export interface EstimateGRPProductionDetail {
    estimate_part_id: number;
    estimate_id: number;
    labour_rate_per_m2: number;
    default_labour_rate_per_m2?: number;
    resolved_labour_rate_per_m2?: number;
    items: EstimateGRPMaterialItem[];
    material_total: number;
    labour_total: number;
    misc_allocation?: number;
    auxiliary_allocation?: number;
    overhead_total: number;
    production_total: number;
    grp_misc_total?: number;
    grp_auxiliary_total?: number;
}

export interface EstimateGRCProductionUpdate {
    design_mix_model_id?: number | null;
    grc_type_id?: number | null;
    labour_rate_per_kg?: number | null;
}


export interface EstimateDetail {
    estimate_id: number;
    estimate_no?: string;
    estimate_ref?: string;
    root_estimate_id?: number;
    revision_no: number;
    inquiry_id?: number;
    customer_id?: number;
    customer_name?: string;
    customer_name_override?: string;
    customer_address?: string;
    customer_address_override?: string;
    enquiry_ref?: string;
    entry_mode?: 'standard' | 'quick_quote' | string;
    quote_mode?: 'lump_sum' | 'itemized' | string;
    project_type_id?: number;
    inquiry_project_type_id?: number;
    project_description?: string;
    date_created?: string;
    attention?: string;
    is_freezed: boolean;
    profit_margin: number;
    vat_percent: number;
    discount_percent: number;
    installation_in_scope?: boolean;
    tooling_reviewed?: boolean;
    production_reviewed?: boolean;
    bom_reviewed?: boolean;
    installation_reviewed?: boolean;
    scope_reviewed?: boolean;
    admin_reviewed?: boolean;
    design_premium_reviewed?: boolean;
    per_hour_labour_rate?: number;
    oh_per_sqm?: number;
    parts: EstimatePart[];
    production_materials?: EstimateProductionMaterial[];
    grg_production_materials?: EstimateProductionMaterial[];
    installation_items?: EstimateInstallationItem[];


    // Header percentage fields
    tooling_consumable_perc?: number;
    tooling_jigs_fixtures_perc?: number;
    tooling_misc_perc?: number;
    logistics_freight_cost_perc?: number;
    logistics_packing_loading_perc?: number;
    admin_contigency_perc?: number;
    admin_warranty_perc?: number;
    admin_retention_perc?: number;
    admin_banking_finance_perc?: number;
    admin_other_cost_perc?: number;

    // Manhour Percentage Fields
    skilled_manhours_perc?: number;
    unskilled_manhours_perc?: number;
    non_working_manhours_perc?: number;
    non_working_hours_per_week?: number;

    // Project Management Fixed Costs
    pm_design_cost?: number;
    pm_project_manager_cost?: number;
    pm_site_intervention_cost?: number;
    pm_documentation_cost?: number;
    pm_travel_cost?: number;

    // Setup Content
    validity?: string;
    annexure_a?: string;
    annexure_b?: string;

    // GRC/Production specifics
    prod_aed_per_kg?: number;
    prod_aed_per_kg_grg?: number;
    prod_aed_per_m2_grp?: number;
    grp_misc_consumables_perc?: number;
    apply_auxiliary_to_grc?: boolean;
    apply_auxiliary_to_grp?: boolean;
    direct_wastage_perc?: number;
    direct_painting_labour_perc?: number;
    direct_electricity_perc?: number;
    direct_acid_wash_labour_perc?: number;
    direct_styro_foam?: number;
    direct_production_accessories?: number;
    grp_stalling_perc?: number;
    total_price?: number;
    total_price_incl_vat?: number;
    net_profit?: number;
    labour_rate?: number; // Added to match Legacy usage
    design_premium?: number;
    design_mix_name?: string;
    grc_type_name?: string;
    design_mix_model_id?: number;
    grc_type_id?: number;
    approval_status_id?: number;
    related_orders?: SaleOrder[];
    related_invoices?: Invoice[];
    related_delivery_notes?: DeliveryNoteSummary[];
}

export interface EstimateSummary {
    estimate_id: number;
    estimate_no?: string;
    estimate_ref?: string;
    root_estimate_id?: number;
    revision_no: number;
    customer_id?: number;
    customer_name?: string;
    customer_name_override?: string;
    date_created?: string;
    attention?: string;
    is_freezed: boolean;
    approval_status_id?: number;
    inquiry_id?: number;
    project_type?: string;
    project_description?: string;
    avg_price_per_sqm?: number;
    total_price: number;
    total_price_incl_vat?: number;
    net_profit: number;
    total_sqm: number;
    parent_estimate_id?: number;
    entry_mode?: 'standard' | 'quick_quote' | string;
    quote_mode?: 'lump_sum' | 'itemized' | string;
}

export interface EstimatePartReviewStateUpdate {
    estimate_part_id: number;
    tooling_reviewed?: boolean;
    production_reviewed?: boolean;
    bom_reviewed?: boolean;
}

export interface EstimateReviewStateUpdate {
    tooling_reviewed?: boolean;
    production_reviewed?: boolean;
    bom_reviewed?: boolean;
    installation_reviewed?: boolean;
    scope_reviewed?: boolean;
    admin_reviewed?: boolean;
    design_premium_reviewed?: boolean;
    part_reviews?: EstimatePartReviewStateUpdate[];
}

const BASE_URL = API_BASE_URL;

const fetchMasterData = async (tableName: string): Promise<any[]> => {
    const response = await fetch(`${BASE_URL}/master-data/${tableName}`);
    if (!response.ok) {
        throw new Error(`Failed to fetch ${tableName}`);
    }
    return response.json();
};

const getApiErrorMessage = async (res: Response, fallback: string): Promise<string> => {
    const data = await res.json().catch(() => null as any);
    if (!data) return fallback;
    if (typeof data.message === 'string' && data.message.trim()) return data.message;
    if (typeof data.detail === 'string' && data.detail.trim()) return data.detail;
    if (typeof data.error_code === 'string' && typeof data.message === 'string') {
        return `${data.message}`;
    }
    if (data.detail && typeof data.detail === 'object') {
        if (typeof data.detail.message === 'string') return data.detail.message;
        if (typeof data.detail.error === 'string') return data.detail.error;
    }
    return fallback;
};

const ensureGrgMaterialTypes = <T extends { material_type_id: number; material_type: string }>(types: T[]): T[] => {
    const list = [...(types || [])];
    if (!list.some((item) => Number(item.material_type_id) === 3 || String(item.material_type || '').toUpperCase() === 'GRG')) {
        list.push({ material_type_id: 3, material_type: 'GRG' } as T);
    }
    return list;
};

type ApiErrorData = Record<string, any>;

export interface LegacyApiError extends Error {
    status?: number;
    error_code?: string;
    details?: ApiErrorData;
    data?: any;
}

const extractApiErrorPayload = (data: any, fallback: string): { message: string; errorCode?: string; details?: ApiErrorData } => {
    const root = (data && typeof data === 'object') ? data as ApiErrorData : {};
    const detailObj = (root.detail && typeof root.detail === 'object') ? root.detail as ApiErrorData : null;

    const message =
        (typeof root.message === 'string' && root.message.trim()) ? root.message :
            (typeof root.detail === 'string' && root.detail.trim()) ? root.detail :
                (detailObj && typeof detailObj.message === 'string' && detailObj.message.trim()) ? detailObj.message :
                    (detailObj && typeof detailObj.detail === 'string' && detailObj.detail.trim()) ? detailObj.detail :
                        fallback;

    const errorCode =
        (typeof root.error_code === 'string' && root.error_code.trim()) ? root.error_code :
            (detailObj && typeof detailObj.error_code === 'string' && detailObj.error_code.trim()) ? detailObj.error_code :
                undefined;

    const details =
        (root.details && typeof root.details === 'object') ? root.details as ApiErrorData :
            (detailObj?.details && typeof detailObj.details === 'object') ? detailObj.details as ApiErrorData :
                undefined;

    return { message, errorCode, details };
};

// ... existing types ...

export interface RelatedOpenSaleOrder {
    so_id: number;
    so_number: string;
    so_date?: string;
    customer_po_no?: string;
    so_status_id: number;
    status_name: string;
    dispatch_progress_id: number;
    dispatch_progress_name: string;
    ordered_qty: number;
    dispatched_qty: number;
    pending_dispatch_qty: number;
}

export interface SaleOrder {
    so_id: number;
    estimate_id: number;
    entry_mode?: 'standard' | 'quick_quote' | string;
    customer_id?: number;
    so_date?: string;
    customer_po_no?: string;
    po_issue_date?: string;
    so_status_id?: number;
    so_remarks?: string; // Added field
    po_remarks?: string;
    customer_name?: string;
    estimate_ref?: string;
    so_number?: string;
    estimate_number?: string;
    total_amount?: number;
    vat_percent?: number;
    discount_percent?: number;
    status_name?: string;
    dispatch_progress_id?: number;
    dispatch_progress_name?: string;
    invoice_progress_id?: number;
    invoice_progress_name?: string;
    payment_status?: 'Not Invoiced' | 'Draft' | 'Pending' | 'Partially Paid' | 'Paid' | 'Cancelled';
    paid_amount?: number;
    outstanding_amount?: number;
    auto_status_id?: number;
    auto_status_name?: string;
    status_is_manual_override?: boolean;
    flow_mode?: 'SIMPLE' | 'PRODUCTION' | string | null;
    resolved_flow_mode?: 'SIMPLE' | 'PRODUCTION' | string | null;
    flow_mode_locked?: boolean;
    can_edit_lines?: boolean;
    can_edit_price?: boolean;
    edit_lock_reason?: string;
    first_downstream_doc_type?: string;
    first_downstream_doc_id?: number;
    related_open_orders?: RelatedOpenSaleOrder[];
    override_history?: {
        id?: number;
        so_detail_id?: number;
        field_name?: string;
        old_value?: number;
        new_value?: number;
        reason?: string;
        changed_by?: number;
        changed_at?: string;
        source?: string;
    }[];
    details?: {
        so_detail_id?: number;
        estimate_part_id?: number;
        item_id?: number;
        line_source?: string;
        part_name: string;
        part_ref_code?: string;
        formatted_part_id?: string;
        item_code?: string;
        quantity: number;
        delivered_qty?: number;
        invoiced_qty?: number;
        remaining_qty?: number;
        remaining_invoice_qty?: number;
        unit_price: number;
        amount: number;
        unit?: string;
        unit_label?: string;
        material_name?: string;
        item_name?: string;
        material_type_id?: number;
        material_type?: string;
        thickness?: number;
        category?: string;
        color_finish?: string;
    }[];
}

export interface SaleOrderFlowGuards {
    can_start_production?: boolean;
    start_production_reason?: string;
    can_create_work_order?: boolean;
    create_work_order_reason?: string;
    can_mark_ready_to_ship?: boolean;
    ready_to_ship_reason?: string;
    can_create_delivery?: boolean;
    create_delivery_reason?: string;
    can_create_proforma_invoice?: boolean;
    create_proforma_reason?: string;
    can_create_tax_invoice?: boolean;
    create_tax_reason?: string;
    can_close?: boolean;
    close_reason?: string;
    dn_dispatch_count?: number;
    wo_linked_count?: number;
    wo_completed_count?: number;
    deliverable_lines_count?: number;
    delivered_not_invoiced_qty?: number;
    flow_mode?: 'SIMPLE' | 'PRODUCTION' | string | null;
    resolved_flow_mode?: 'SIMPLE' | 'PRODUCTION' | string | null;
    flow_mode_locked?: boolean;
    selective_mode_enabled?: boolean;
    simple_mode_enabled?: boolean;
    production_bypassed?: boolean;
    can_edit_lines?: boolean;
    can_edit_price?: boolean;
    edit_lock_reason?: string;
    first_downstream_doc_type?: string;
    first_downstream_doc_id?: number;
}

export interface SaleOrderOverview {
    so_id: number;
    order_no?: string;
    estimate_id?: number;
    customer_id?: number;
    customer_name?: string;
    so_date?: string;
    status?: {
        so_status_id?: number;
        status_name?: string;
        dispatch_progress_id?: number;
        dispatch_progress_name?: string;
        invoice_progress_id?: number;
        invoice_progress_name?: string;
    };
    totals?: {
        ordered_qty?: number;
        delivered_qty?: number;
        invoiced_qty?: number;
        remaining_delivery_qty?: number;
        dispatched_qty?: number;
        draft_planned_qty?: number;
        balance_to_dispatch_qty?: number;
        order_amount?: number;
    };
    delivery_counts?: {
        dispatched?: number;
        draft?: number;
    };
    deliveries?: Array<{
        dn_id: number;
        dn_number?: string;
        dn_date?: string;
        status_id?: number;
        status_name?: string;
        item_count?: number;
    }>;
    invoices?: Array<{
        invoice_id: number;
        invoice_date?: string;
        invoice_type?: 'TAX' | 'PROFORMA' | string;
        status_id?: number;
        status_name?: string;
        grand_total?: number;
    }>;
    work_orders?: any[];
    details?: any[];
    related_open_orders?: RelatedOpenSaleOrder[];
    warnings?: string[];
    flow_guards?: SaleOrderFlowGuards;
    line_edit_guard?: {
        can_edit_lines?: boolean;
        can_edit_price?: boolean;
        edit_lock_reason?: string;
        first_downstream_doc_type?: string;
        first_downstream_doc_id?: number;
    };
}

export interface WorkOrderItem {
    wo_item_id: number;
    wo_id: number;
    description?: string;
    quantity: number;
    uom?: string;
}

export interface WorkOrder {
    wo_id: number;
    wo_number: string;
    estimate_id?: number;
    so_id?: number;
    source_type?: 'ESTIMATE' | 'SO' | 'BOM' | 'UNKNOWN';
    customer_id?: number;
    wo_date?: string;
    status_id: number;
    remarks?: string;
    customer_name?: string;
    estimate_ref?: string;
    items?: WorkOrderItem[];
}

export interface Invoice {
    invoice_id: number;
    estimate_id: number;
    estimate_ref?: string;
    customer_id?: number;
    so_id?: number;
    dn_id?: number;
    dn_number?: string;
    wo_id?: number;
    wo_number?: string;
    source_summary?: string;
    invoice_type?: 'TAX' | 'PROFORMA';
    status_id?: number;
    status_name?: string;
    invoice_date?: string;
    customer_po_no?: string;
    customer_name?: string;
    customer_address?: string; // Added field
    customer_emirate?: string;
    customer_trn?: string;     // Added field
    invoice_notes?: string;

    // Gap Closure
    sub_total?: number;
    discount_amount?: number;
    vat_percent?: number;
    vat_amount?: number;
    grand_total?: number;
    estimate_revision_no?: number;

    total_qty?: number;
    avg_unit_price?: number;
    discount_percent?: number;
    paid_amount?: number;
    outstanding_amount?: number;
    payment_progress?: 'Not Paid' | 'Partially Paid' | 'Fully Paid';
    suggest_close_so_id?: number;
    credit_notes?: CreditNoteSummary[];
    linked_dns?: {
        dn_id: number;
        dn_number?: string | null;
        dn_date?: string | null;
        status_id?: number | null;
    }[];
    details?: any[];
}

export interface CreditNoteSummary {
    cn_id: number;
    cn_number: string;
    cn_date?: string;
    status_id: number;
    status_name: string;
    grand_total: number;
    posted_invoice_id?: number;
}

export type CreditNoteMode = 'RETURN_ITEMS' | 'CHARGEBACK_ADJUSTMENT';

export interface CreditNoteLine {
    cn_detail_id: number;
    source_invoice_detail_id: number;
    estimate_part_id?: number;
    part_name: string;
    quantity: number;
    unit_price: number;
    line_total: number;
    item_id?: number;
}

export interface CreditNoteStatusHistory {
    log_id: number;
    old_status_id: number;
    old_status_name: string;
    new_status_id: number;
    new_status_name: string;
    reason?: string;
    changed_by_user_id?: number;
    created_at?: string;
}

export interface CreditNote {
    cn_id: number;
    cn_number: string;
    source_invoice_id: number;
    customer_id: number;
    customer_name?: string;
    cn_date?: string;
    status_id: number;
    status_name: string;
    reason?: string;
    remarks?: string;
    posted_invoice_id?: number;
    posted_journal_id?: number;
    subtotal: number;
    vat_percent: number;
    vat_amount: number;
    grand_total: number;
    created_by?: number;
    created_at?: string;
    posted_at?: string;
    cancelled_at?: string;
    lines: CreditNoteLine[];
    history: CreditNoteStatusHistory[];
}

export type StatusVisualToken =
    | 'so-draft'
    | 'so-confirmed'
    | 'so-production'
    | 'so-ready'
    | 'so-dispatched'
    | 'so-closed'
    | 'so-cancelled'
    | 'wo-planned'
    | 'wo-in-progress'
    | 'wo-completed'
    | 'wo-cancelled'
    | 'so-other'
    | 'wo-other'
    | 'neutral';

export interface InvoiceSourceOption {
    source_key: string;
    so_id: number;
    so_number: string;
    so_status_id: number;
    so_status_label: string;
    customer_id?: number;
    customer_name?: string;
    wo_id?: number;
    wo_number?: string;
    wo_status_id?: number;
    wo_status_label?: string;
    color_token: StatusVisualToken;
}

export interface InvoiceSourceFilter {
    query?: string;
    customer_id?: number;
    so_status_ids?: number[];
    wo_status_ids?: number[];
    limit?: number;
    offset?: number;
}

export interface UnbilledDeliveryLine {
    estimate_part_id?: number | null;
    so_detail_id?: number | null;
    item_id?: number | null;
    part_name: string;
    quantity: number;
    unit_price: number;
    line_total: number;
}

export interface UnbilledDeliveryDn {
    dn_id: number;
    dn_number: string;
    dn_date?: string | null;
    status_id?: number | null;
    status_name?: string | null;
    included: boolean;
    reason?: string | null;
    delivered_qty: number;
    already_invoiced_qty: number;
    invoiceable_qty: number;
}

export interface UnbilledDeliveryPreview {
    so_id: number;
    mode: 'spot' | 'cutoff';
    dn_id?: number | null;
    cutoff_date?: string | null;
    can_create: boolean;
    reason?: string | null;
    included_dns: UnbilledDeliveryDn[];
    excluded_dns: UnbilledDeliveryDn[];
    lines: UnbilledDeliveryLine[];
    subtotal: number;
    sub_total?: number;
    discount_amount: number;
    vat_percent: number;
    vat_amount: number;
    grand_total: number;
    active_tax_invoice_count: number;
    posted_tax_invoice_count: number;
    existing_active_tax_invoice_count?: number;
    existing_posted_invoice_count?: number;
}

export interface UnbilledDeliveryInvoiceCreateRequest {
    so_id: number;
    mode: 'spot' | 'cutoff';
    dn_id?: number;
    cutoff_date?: string;
    invoice_date?: string;
    invoice_notes?: string;
}

export interface PurchaseOrder {
    po_id: number;
    supplier_id?: number;
    supplier_quotation_no?: string;
    po_date?: string;
    delivery_date?: string;
    delivery_address?: string;
    delivery_phone_no?: string;
    payment_terms?: string;
    payment_method?: string;
    discount_amount?: number;
    vat?: number;
    supplier_name?: string;
    remarks?: string;
    details: PODetail[];

    // Enriched
    po_number?: string;
    status_name?: string;
    po_amount?: number;
    po_status_id?: number;
}



export interface PODetail {
    po_detail_id: number;
    po_id: number;
    material_id: number;
    material_name?: string;
    material_code?: string;
    description: string;
    unit_id?: number;
    unit_name?: string;
    quantity: number;
    unit_price: number;
    sub_total?: number;
    total_qty_received?: number;
}

export interface GRN {
    grn_id: number;
    po_id: number;
    date_received?: string;
    received_by?: string;
    delivery_note_no?: string;
    remarks?: string;
    supplier_id?: number;
    supplier_name?: string;

    // Enriched
    grn_number?: string;
    po_number?: string;
    status_id?: number;
    status_name?: string;
    total_received_qty?: number;
    source_path?: string;
    linked_bill_id?: number;
    bill_status_id?: number;
    bill_status_name?: string;
    can_create_bill?: boolean;
    create_bill_reason?: string;
    can_cancel?: boolean;
    cancel_reason?: string;
    can_delete?: boolean;
    delete_reason?: string;
    auto_bill_created?: boolean;
    auto_bill_id?: number;
    auto_bill_status?: string;
    warning?: string;
}

export interface GRNDetail {
    grn_detail_id: number;
    grn_id: number;
    po_detail_id?: number;
    material_id?: number;
    description?: string;
    unit_id?: number;
    unit_name?: string;
    qty_ordered?: number;
    total_qty_received?: number;
    remaining_qty?: number;
    quantity_received: number;
    unit_price?: number;
    sub_total?: number;
    transaction_unit_id?: number;
    transaction_unit_name?: string;
    transaction_qty?: number;
    base_qty?: number;
    conversion_factor?: number;
    base_unit_price?: number;
    material_desc?: string;
}

export type Supplier = LegacySupplier;

export interface LegacySupplier {
    supplier_id: number;
    supplier_name: string;
    poc_name?: string;
    poc_contact?: string;
    emirate?: string;
    office_tel?: string;
    email_address?: string;
    trn?: string;
    address?: string;
}

export interface SupplierBillDetail {
    bill_detail_id: number;
    bill_id: number;
    po_detail_id?: number;
    material_id?: number;
    description?: string;
    quantity: number;
    unit_price: number;
    sub_total: number;
}

export interface SupplierBill {
    bill_id: number;
    supplier_id: number;
    grn_id: number;
    po_id: number;
    source_bill_id?: number;
    bill_date: string;
    reference_no: string;
    remarks?: string;
    discount_amount?: number;
    vat_percent: number;
    vat_amount: number;
    sub_total: number;
    grand_total: number;
    status_id: number;
    status_name?: string;
    paid_amount?: number;
    outstanding_amount?: number;
    payment_progress?: 'Not Paid' | 'Partially Paid' | 'Fully Paid';
    source_type?: string;
    source_grn_id?: number;
    is_auto_draft?: boolean;
    auto_created_at?: string;
    supplier_name: string;
    details?: SupplierBillDetail[];
    vat_treatment?: string;
    input_vat_recovery_percent?: number | null;
    recoverable_vat_amount?: number | null;
    vat_recovery_status?: 'UNREVIEWED' | 'CONFIRMED' | string;
    nonrecoverable_vat_account_id?: number | null;
}

export interface SupplierBillUpdatePayload {
    bill_date?: string;
    reference_no?: string;
    remarks?: string;
    discount_amount?: number;
    vat_percent?: number;
    details?: Array<{
        bill_detail_id: number;
        quantity: number;
        unit_price: number;
    }>;
}

export interface PurchasingFlowGuards {
    can_create_grn: boolean;
    create_grn_reason: string;
    can_create_bill: boolean;
    create_bill_reason: string;
    can_post_bill: boolean;
    post_bill_reason: string;
    can_pay_bill: boolean;
    pay_bill_reason: string;
    can_close?: boolean;
    close_reason?: string;
    grn_count: number;
    bill_count: number;
    posted_bill_count: number;
    paid_bill_count: number;
    inventory_impact_status?: string;
    financial_impact_status?: string;
}

export interface PurchaseOrderOverview {
    po_id: number;
    po_number: string;
    supplier_id?: number | null;
    supplier_name?: string;
    po_status_id: number;
    po_status_name: string;
    po_date?: string;
    delivery_date?: string;
    next_action?: string;
    flow_guards: PurchasingFlowGuards;
}

export interface GrnOverview {
    grn_id: number;
    grn_number: string;
    po_id: number;
    po_number: string;
    supplier_id?: number | null;
    supplier_name?: string;
    po_status_id: number;
    po_status_name: string;
    status_id: number;
    status_name: string;
    source_path: 'PO_GRN' | 'DIRECT_GRN' | string;
    bill_exists: boolean;
    bill_id?: number | null;
    bill_status?: string | null;
    next_action?: string;
    can_create_bill: boolean;
    create_bill_reason: string;
    can_cancel: boolean;
    cancel_reason: string;
    can_delete: boolean;
    delete_reason: string;
    inventory_impact_status?: string;
    financial_impact_status?: string;
}

export interface WorkflowStatusDefinition {
    status_id: number;
    status_label: string;
    status_meaning: string;
    terminal: boolean;
}

export interface WorkflowEventDefinition {
    event_code: string;
    event_label: string;
    event_description: string;
    from_statuses: number[];
    to_status: number;
    to_status_label?: string;
    permission_key?: string;
    branch_effects?: string[];
    override?: boolean;
}

export interface WorkflowEntityDefinition {
    entity: string;
    entity_label: string;
    statuses: WorkflowStatusDefinition[];
    events: WorkflowEventDefinition[];
}

export interface WorkflowNextActionsResponse {
    entity: string;
    entity_id: number;
    current_status_id: number;
    current_status_name: string;
    next_actions: WorkflowEventDefinition[];
}

export interface WorkflowHistoryEntry {
    log_id: number;
    old_status_id: number;
    old_status_name: string;
    new_status_id: number;
    new_status_name: string;
    event_code?: string | null;
    event_label?: string | null;
    changed_by_user_id?: number | null;
    reason?: string | null;
    is_override: boolean;
    created_at?: string | null;
}

export interface WorkflowBranchEffectDetail {
    code: string;
    title: string;
    impact_module: string;
    message: string;
}

export interface WorkflowPrecheckItem {
    key: string;
    status: 'pass' | 'warn' | 'fail' | string;
    message: string;
    permission_key?: string;
}

export interface WorkflowContextAction extends WorkflowEventDefinition {
    permission_granted?: boolean;
    precheck_status?: 'pass' | 'warn' | 'fail' | string;
    prechecks?: WorkflowPrecheckItem[];
    branch_effect_details?: WorkflowBranchEffectDetail[];
}

export interface WorkflowContextResponse {
    entity: string;
    entity_id: number;
    entity_label: string;
    current_status_id: number;
    current_status_name: string;
    current_status_meaning?: string;
    path_key?: string;
    path_label?: string;
    current_step_key?: string;
    current_step_label?: string;
    lineage?: Record<string, any>;
    handoff_hint?: string;
    recommended_action?: WorkflowContextAction | null;
    next_actions: WorkflowContextAction[];
    generated_at?: string;
}

export interface WorkflowPrecheckResponse {
    entity: string;
    entity_id: number;
    action: string;
    overall_status: 'pass' | 'warn' | 'fail' | string;
    current_status_id: number;
    current_status_name: string;
    checks: WorkflowPrecheckItem[];
    branch_effect_details: WorkflowBranchEffectDetail[];
}

export interface WorkflowPlaybookStep {
    step_key: string;
    label: string;
    entity?: string;
    route_hint?: string;
    prerequisites?: string[];
}

export interface WorkflowPlaybookPath {
    path_key: string;
    label: string;
    description?: string;
    route_hint?: string;
    steps: WorkflowPlaybookStep[];
}

export interface WorkflowPlaybookModule {
    module_key: string;
    module_label: string;
    description?: string;
    paths: WorkflowPlaybookPath[];
}

export interface WorkflowPlaybookResponse {
    version: string;
    generated_at: string;
    modules: WorkflowPlaybookModule[];
}

export const legacyApi = {
    // ... existing
    suppliers: {
        list: async (): Promise<LegacySupplier[]> => {
            const res = await fetch(`${BASE_URL}/legacy/suppliers`);
            if (!res.ok) throw new Error('Failed to fetch suppliers');
            return res.json();
        },
        create: async (data: any): Promise<LegacySupplier> => {
            const res = await fetch(`${BASE_URL}/legacy/suppliers`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!res.ok) throw new Error('Failed to create supplier');
            return res.json();
        },
        update: async (id: number, data: any): Promise<any> => {
            const res = await fetch(`${BASE_URL}/legacy/suppliers/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!res.ok) throw new Error('Failed to update supplier');
            return res.json();
        },
        delete: async (id: number): Promise<void> => {
            const res = await fetch(`${BASE_URL}/legacy/suppliers/${id}`, { method: 'DELETE' });
            if (!res.ok) throw new Error('Failed to delete supplier');
        }
    },
    customers: {
        list: async (): Promise<Customer[]> => {
            const res = await fetch(`${BASE_URL}/customers`);
            if (!res.ok) throw new Error('Failed to fetch customers');
            return res.json();
        },
        listPaged: async (filters?: {
            q?: string;
            emirate?: string;
            credit?: 'all' | 'with-credit' | 'no-credit';
            limit?: number;
            offset?: number;
        }): Promise<PagedResult<Customer>> => {
            const params = new URLSearchParams();
            if (filters?.q) params.set('q', filters.q);
            if (filters?.emirate) params.set('emirate', filters.emirate);
            if (filters?.credit) params.set('credit', filters.credit);
            if (typeof filters?.limit === 'number') params.set('limit', String(filters.limit));
            if (typeof filters?.offset === 'number') params.set('offset', String(filters.offset));
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await fetch(`${BASE_URL}/customers/paged${suffix}`);
            if (!res.ok) throw new Error('Failed to fetch customers');
            return res.json();
        },
        create: async (data: any): Promise<Customer> => {
            const res = await fetch(`${BASE_URL}/customers`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!res.ok) throw new Error('Failed to create customer');
            return res.json();
        },
        update: async (id: number, data: any): Promise<Customer> => {
            const res = await fetch(`${BASE_URL}/customers/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!res.ok) throw new Error('Failed to update customer');
            return res.json();
        },
        delete: async (id: number): Promise<void> => {
            const res = await fetch(`${BASE_URL}/customers/${id}`, { method: 'DELETE' });
            if (!res.ok) throw new Error('Failed to delete customer');
        }
    },
    workflow: {
        listEntities: async (): Promise<Array<{ entity: string; entity_label: string }>> => {
            const res = await fetch(`${BASE_URL}/workflow/entities`);
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to fetch workflow entities'));
            return res.json();
        },
        getDefinition: async (entity: string): Promise<WorkflowEntityDefinition> => {
            const res = await fetch(`${BASE_URL}/workflow/${encodeURIComponent(entity)}/definition`);
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to fetch workflow definition'));
            return res.json();
        },
        getNextActions: async (entity: string, id: number): Promise<WorkflowNextActionsResponse> => {
            const res = await fetch(`${BASE_URL}/workflow/${encodeURIComponent(entity)}/${id}/next-actions`);
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to fetch next actions'));
            return res.json();
        },
        getHistory: async (entity: string, id: number): Promise<{ entity: string; entity_id: number; history: WorkflowHistoryEntry[] }> => {
            const res = await fetch(`${BASE_URL}/workflow/${encodeURIComponent(entity)}/${id}/history`);
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to fetch status history'));
            return res.json();
        },
        getPlaybooks: async (): Promise<WorkflowPlaybookResponse> => {
            const res = await fetch(`${BASE_URL}/workflow/playbooks`);
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to fetch workflow playbooks'));
            return res.json();
        },
        getPlaybook: async (moduleKey: string): Promise<WorkflowPlaybookModule & { version?: string; generated_at?: string }> => {
            const res = await fetch(`${BASE_URL}/workflow/playbooks/${encodeURIComponent(moduleKey)}`);
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to fetch workflow playbook'));
            return res.json();
        },
        getContext: async (entity: string, id: number): Promise<WorkflowContextResponse> => {
            const res = await fetch(`${BASE_URL}/workflow/context/${encodeURIComponent(entity)}/${id}`);
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to fetch workflow context'));
            return res.json();
        },
        precheck: async (payload: { entity: string; entity_id: number; action: string }): Promise<WorkflowPrecheckResponse> => {
            const res = await fetch(`${BASE_URL}/workflow/precheck`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to run workflow precheck'));
            return res.json();
        },
    },
    estimates: {
        list: async (options?: { skip?: number; limit?: number }): Promise<EstimateSummary[]> => {
            const params = new URLSearchParams();
            if (options?.skip !== undefined) params.append('skip', options.skip.toString());
            if (options?.limit !== undefined) params.append('limit', options.limit.toString());
            const res = await fetch(`${BASE_URL}/estimates?${params.toString()}`);
            if (!res.ok) throw new Error('Failed to fetch estimates');
            return res.json();
        },
        listPaged: async (filters?: {
            q?: string;
            status?: string;
            date_from?: string;
            date_to?: string;
            limit?: number;
            offset?: number;
        }): Promise<PagedResult<EstimateSummary>> => {
            const params = new URLSearchParams();
            if (filters?.q) params.set('q', filters.q);
            if (filters?.status) params.set('status', filters.status);
            if (filters?.date_from) params.set('date_from', filters.date_from);
            if (filters?.date_to) params.set('date_to', filters.date_to);
            if (typeof filters?.limit === 'number') params.set('limit', String(filters.limit));
            if (typeof filters?.offset === 'number') params.set('offset', String(filters.offset));
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await fetch(`${BASE_URL}/estimates/paged${suffix}`);
            if (!res.ok) throw new Error('Failed to fetch estimates');
            return res.json();
        },
        getDetail: async (id: number): Promise<EstimateDetail> => { // Changed to EstimateDetail as Estimate is undefined
            const response = await fetch(`${API_BASE_URL}/estimates/${id}`);
            if (!response.ok) throw new Error('Failed to fetch estimate detail');
            return response.json();
        },
        calculate: async (payload: CalculateRequest): Promise<CalculationResult> => {
            const response = await fetch(`${API_BASE_URL}/estimates/calculate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!response.ok) throw new Error('Calculation failed');
            return response.json();
        },
        calculateLegacy: async (payload: CalculateRequest): Promise<CalculationResult> => {
            const response = await fetch(`${API_BASE_URL}/estimates/calculate-legacy`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!response.ok) throw new Error('Legacy calculation failed');
            return response.json();
        },
        create: async (payload: CalculateRequest): Promise<EstimateSummary> => {
            const response = await fetch(`${API_BASE_URL}/estimates/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!response.ok) throw new Error('Failed to create estimate');
            return response.json();
        },
        update: async (id: number, data: CalculateRequest): Promise<EstimateSummary> => {
            const response = await fetch(`${API_BASE_URL}/estimates/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                console.error("API Error Details:", JSON.stringify(err, null, 2));
                throw new Error(err.detail ? JSON.stringify(err.detail) : 'Failed to update estimate');
            }
            return response.json();
        },
        updateReviewState: async (id: number, data: EstimateReviewStateUpdate): Promise<void> => {
            const response = await fetch(`${API_BASE_URL}/estimates/${id}/review-state`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to update review state');
            }
        },
        updateLabourCost: async (id: number, labour_rate: number): Promise<void> => {
            const response = await fetch(`${API_BASE_URL}/estimates/${id}/update-labour-cost`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ labour_rate })
            });
            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.detail || 'Failed to update labour cost');
            }
        },
        updateDesignMix: async (id: number, design_mix_id: number, grc_type_id: number): Promise<void> => {
            const response = await fetch(`${API_BASE_URL}/estimates/${id}/update-design-mix`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ design_mix_id, grc_type_id })
            });
            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.detail || 'Failed to update design mix');
            }
        },
        async updateAdminControls(id: number, data: AdminControlsUpdate): Promise<any> {
            const response = await fetch(`${API_BASE_URL}/estimates/${id}/admin-controls`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to update admin controls');
            }
            return response.json();
        },
        async updateInstallations(id: number, items: InstallationItemUpdate[]): Promise<any> {
            const response = await fetch(`${API_BASE_URL}/estimates/${id}/installations`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items })
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to update installations');
            }
            return response.json();
        },
        async updateProductionSettings(id: number, data: ProductionSettingsUpdate): Promise<any> {
            const response = await fetch(`${API_BASE_URL}/estimates/${id}/production-settings`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to update production settings');
            }
            return response.json();
        },
        async loadInstallationFactors(id: number): Promise<void> {
            const response = await fetch(`${API_BASE_URL}/estimates/${id}/load-installation-factors`, {
                method: 'POST'
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to load installation factors');
            }
        },
        listGrpMaterialsCatalog: async (): Promise<GRPMaterialCatalogItem[]> => {
            const response = await fetch(`${API_BASE_URL}/estimates/grp-materials`);
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to fetch GRP materials catalog');
            }
            return response.json();
        },
        getGrpMaterials: async (estimate_part_id: number): Promise<EstimateGRPMaterialItem[]> => {
            const response = await fetch(`${API_BASE_URL}/estimates/parts/${estimate_part_id}/grp-materials`);
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to fetch GRP materials');
            }
            return response.json();
        },
        getGrpProduction: async (estimate_part_id: number): Promise<EstimateGRPProductionDetail> => {
            const response = await fetch(`${API_BASE_URL}/estimates/parts/${estimate_part_id}/grp-production`);
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to fetch GRP production');
            }
            return response.json();
        },
        updateGrpMaterials: async (estimate_part_id: number, items: EstimateGRPMaterialItem[]): Promise<any> => {
            const response = await fetch(`${API_BASE_URL}/estimates/parts/${estimate_part_id}/grp-materials`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items })
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to save GRP materials');
            }
            return response.json();
        },
        updateGrpProduction: async (estimate_part_id: number, labour_rate_per_m2: number, items: EstimateGRPMaterialItem[]): Promise<EstimateGRPProductionDetail> => {
            const response = await fetch(`${API_BASE_URL}/estimates/parts/${estimate_part_id}/grp-production`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ labour_rate_per_m2, items })
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to save GRP production');
            }
            return response.json();
        },
        updateGrcProduction: async (estimate_part_id: number, data: EstimateGRCProductionUpdate): Promise<any> => {
            const response = await fetch(`${API_BASE_URL}/estimates/parts/${estimate_part_id}/grc-production`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to save GRC production overrides');
            }
            return response.json();
        },
        linkPartItem: async (estimateId: number, partId: number, itemId: number | null): Promise<any> => {
            const response = await fetch(`${API_BASE_URL}/estimates/${estimateId}/parts/${partId}/item-link`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ item_id: itemId })
            });
            if (!response.ok) throw new Error(await getApiErrorMessage(response, 'Failed to link estimate part to item'));
            return response.json();
        },
        getMappingGaps: async (estimateId: number): Promise<any> => {
            const response = await fetch(`${API_BASE_URL}/estimates/${estimateId}/mapping-gaps`);
            if (!response.ok) throw new Error(await getApiErrorMessage(response, 'Failed to fetch mapping gaps'));
            return response.json();
        },
        revise: async (id: number): Promise<EstimateSummary> => {
            const response = await fetch(`${API_BASE_URL}/estimates/${id}/revise`, {
                method: 'POST'
            });
            if (!response.ok) throw new Error('Failed to create revision');
            return response.json();
        },
        markSent: async (id: number): Promise<void> => {
            let response = await fetch(`${API_BASE_URL}/estimates/${id}/transition`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'submit', reason: 'Marked as Sent' })
            });
            if (response.status === 404) {
                response = await fetch(`${API_BASE_URL}/estimates/${id}/sent`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' }
                });
            }
            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.detail || 'Failed to mark as sent');
            }
        },
        listRevisions: async (id: number): Promise<EstimateSummary[]> => {
            const response = await fetch(`${API_BASE_URL}/estimates/${id}/revisions`);
            if (!response.ok) throw new Error('Failed to fetch revisions');
            return response.json();
        },
        freeze: async (id: number, freeze: boolean): Promise<EstimateSummary> => {
            const response = await fetch(`${API_BASE_URL}/estimates/${id}/freeze`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ freeze })
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to update freeze state');
            }
            return response.json();
        },
        delete: async (id: number): Promise<void> => {
            const response = await fetch(`${API_BASE_URL}/estimates/${id}`, {
                method: 'DELETE'
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                console.error("API Error Details:", JSON.stringify(err, null, 2));
                throw new Error(err.detail ? JSON.stringify(err.detail) : 'Failed to update estimate');
            }
        },
        downloadPDF: async (id: number) => {
            const printUrl = `${API_BASE_URL}/estimates/${id}/print?autoprint=1`;
            const printWindow = window.open(printUrl, '_blank');
            if (!printWindow) {
                throw new Error('Pop-up blocked. Please allow pop-ups and try again.');
            }
            printWindow.focus();
        },
        emailPdf: async (id: number, payload: EmailPdfRequest): Promise<EmailPdfResponse> => {
            const response = await fetch(`${API_BASE_URL}/estimates/${id}/email-pdf`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.detail || err.message || 'Failed to send estimate PDF');
            }
            return response.json();
        },
        createShareLink: async (id: number, payload?: EstimateShareLinkRequest): Promise<EstimateShareLinkResponse> => {
            const response = await fetch(`${API_BASE_URL}/estimates/${id}/share-link`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload || {}),
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.detail || err.message || 'Failed to create quotation share link');
            }
            return response.json();
        },
        revokeShareLink: async (id: number): Promise<void> => {
            const response = await fetch(`${API_BASE_URL}/estimates/${id}/share-link/revoke`, {
                method: 'POST',
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.detail || err.message || 'Failed to revoke quotation share link');
            }
        },
        exportJournals: async () => {
            const response = await fetch(`${BASE_URL}/reports/export/journals`);
            if (!response.ok) throw new Error('Failed to download journals');
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Journal_Export_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
        }
    },
    sales: {
        list: async (estimateId?: number): Promise<SaleOrder[]> => {
            const url = estimateId
                ? `${BASE_URL}/sales/orders?estimate_id=${estimateId}`
                : `${BASE_URL}/sales/orders`;
            const res = await fetch(url);
            if (!res.ok) throw new Error('Failed to fetch sale orders');
            return res.json();
        },
        // Alias for backward compatibility if needed, or replace usages
        listOrders: async (): Promise<SaleOrder[]> => {
            return legacyApi.sales.list();
        },
        listOrdersPaged: async (filters?: {
            estimate_id?: number;
            q?: string;
            status_id?: number;
            payment_status?: string;
            pending_dispatch?: boolean;
            date_from?: string;
            date_to?: string;
            limit?: number;
            offset?: number;
        }): Promise<PagedResult<SaleOrder>> => {
            const params = new URLSearchParams();
            if (typeof filters?.estimate_id === 'number') params.set('estimate_id', String(filters.estimate_id));
            if (filters?.q) params.set('q', filters.q);
            if (typeof filters?.status_id === 'number') params.set('status_id', String(filters.status_id));
            if (filters?.payment_status) params.set('payment_status', filters.payment_status);
            if (filters?.pending_dispatch) params.set('pending_dispatch', 'true');
            if (filters?.date_from) params.set('date_from', filters.date_from);
            if (filters?.date_to) params.set('date_to', filters.date_to);
            if (typeof filters?.limit === 'number') params.set('limit', String(filters.limit));
            if (typeof filters?.offset === 'number') params.set('offset', String(filters.offset));
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await fetch(`${BASE_URL}/sales/orders/paged${suffix}`);
            if (!res.ok) throw new Error('Failed to fetch sale orders');
            return res.json();
        },
        listRelatedOpenOrders: async (customerId: number, excludeSoId?: number): Promise<RelatedOpenSaleOrder[]> => {
            const params = new URLSearchParams({ customer_id: String(customerId) });
            if (typeof excludeSoId === 'number' && excludeSoId > 0) params.set('exclude_so_id', String(excludeSoId));
            const res = await fetch(`${BASE_URL}/sales/orders/open-pending?${params.toString()}`);
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to fetch related open sale orders'));
            const data = await res.json();
            return Array.isArray(data?.items) ? data.items : [];
        },
        getOrder: async (id: number): Promise<SaleOrder> => {
            const res = await fetch(`${BASE_URL}/sales/orders/${id}`);
            if (!res.ok) throw new Error('Failed to fetch sale order');
            return res.json();
        },
        listOrderDocuments: async (id: number, category: DocumentCategory = 'LPO'): Promise<DocumentAttachment[]> => {
            const res = await fetch(`${BASE_URL}/sales/orders/${id}/documents?category=${encodeURIComponent(category)}`);
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to fetch sale order documents'));
            const data: DocumentAttachmentListResponse = await res.json();
            return Array.isArray(data?.documents) ? data.documents : [];
        },
        uploadOrderDocument: async (
            id: number,
            payload: { file: File; category?: DocumentCategory; note?: string; mark_current?: boolean }
        ): Promise<DocumentAttachment> => {
            const formData = new FormData();
            formData.append('file', payload.file);
            formData.append('category', payload.category || 'LPO');
            if (payload.note !== undefined) formData.append('note', payload.note);
            formData.append('mark_current', payload.mark_current === false ? 'false' : 'true');
            const res = await fetch(`${BASE_URL}/sales/orders/${id}/documents`, {
                method: 'POST',
                body: formData,
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to upload sale order document'));
            return res.json();
        },
        setOrderDocumentCurrent: async (id: number, docId: number): Promise<DocumentAttachment> => {
            const res = await fetch(`${BASE_URL}/sales/orders/${id}/documents/${docId}/set-current`, {
                method: 'PATCH',
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to set current document'));
            return res.json();
        },
        deleteOrderDocument: async (id: number, docId: number): Promise<void> => {
            const res = await fetch(`${BASE_URL}/sales/orders/${id}/documents/${docId}`, {
                method: 'DELETE',
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to delete document'));
        },
        downloadOrderDocument: async (id: number, docId: number): Promise<void> => {
            const url = `${BASE_URL}/sales/orders/${id}/documents/${docId}/download`;
            const popup = window.open(url, '_blank');
            if (!popup) throw new Error('Pop-up blocked. Please allow pop-ups and try again.');
            popup.focus();
        },
        previewOrderDocumentUrl: (id: number, docId: number): string => `${BASE_URL}/sales/orders/${id}/documents/${docId}/preview`,
        getOrderOverview: async (id: number): Promise<SaleOrderOverview> => {
            const res = await fetch(`${BASE_URL}/sales/orders/${id}/overview`);
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to fetch sale order overview'));
            return res.json();
        },
        getOrderMappingGaps: async (id: number): Promise<any> => {
            const res = await fetch(`${BASE_URL}/sales/orders/${id}/mapping-gaps`);
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to fetch sale order mapping gaps'));
            return res.json();
        },
        deleteOrder: async (id: number): Promise<void> => {
            const res = await fetch(`${BASE_URL}/sales/orders/${id}`, {
                method: 'DELETE'
            });
            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.detail || 'Failed to delete sale order');
            }
        },
        updateOrder: async (id: number, payload: { customer_id?: number; customer_po_no?: string; so_date?: string; po_issue_date?: string; so_remarks?: string; po_remarks?: string; vat_percent?: number; discount_percent?: number; so_status_id?: number; flow_mode?: 'SIMPLE' | 'PRODUCTION'; override_reason?: string; details?: { so_detail_id?: number; estimate_part_id?: number; part_name?: string; quantity: number; unit_price?: number; category?: string; color_finish?: string; item_id?: number }[] }): Promise<SaleOrder> => {
            const res = await fetch(`${BASE_URL}/sales/orders/${id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) {
                throw new Error(await getApiErrorMessage(res, 'Failed to update sale order'));
            }
            return res.json();
        },
        createOrder: async (payload: {
            customer_id: number;
            estimate_id?: number;
            customer_po_no?: string;
            so_date?: string;
            po_issue_date?: string;
            so_status_id?: number;
            so_remarks?: string;
            po_remarks?: string;
            vat_percent?: number;
            discount_percent?: number;
            flow_mode?: 'SIMPLE' | 'PRODUCTION';
            lines: {
                item_id?: number;
                part_name?: string;
                quantity: number;
                unit_price: number;
                category?: string;
                color_finish?: string;
            }[];
        }): Promise<SaleOrder> => {
            const res = await fetch(`${BASE_URL}/sales/orders`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) {
                throw new Error(await getApiErrorMessage(res, 'Failed to create sale order'));
            }
            return res.json();
        },
        transitionOrder: async (id: number, action: string, reason?: string): Promise<any> => {
            const res = await fetch(`${BASE_URL}/sales/orders/${id}/transition`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action, reason })
            });
            if (!res.ok) {
                throw new Error(await getApiErrorMessage(res, 'Failed to transition sale order'));
            }
            return res.json();
        },
        adminReopenOrder: async (id: number, reason?: string): Promise<any> => {
            const res = await fetch(`${BASE_URL}/sales/orders/${id}/admin-reopen`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ reason: reason || 'Admin reopen for correction' }),
            });
            if (!res.ok) {
                throw new Error(await getApiErrorMessage(res, 'Failed to reopen sale order'));
            }
            return res.json();
        },
        convertEstimate: async (payload: { estimate_id: number; customer_po_no?: string; so_date?: string; so_status_id?: number; flow_mode?: 'SIMPLE' | 'PRODUCTION'; allow_duplicate?: boolean; force_credit_override?: boolean }): Promise<SaleOrder> => {
            const res = await fetch(`${BASE_URL}/sales/orders/convert`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) {
                const errData = await res.json().catch(() => ({ detail: 'Failed to convert estimate' }));
                throw new Error(errData.detail || 'Failed to convert estimate');
            }
            return res.json();
        },
        downloadPdf: async (id: number) => {
            const printUrl = `${BASE_URL}/sales/orders/${id}/print?autoprint=1`;
            const printWindow = window.open(printUrl, '_blank');
            if (!printWindow) throw new Error('Pop-up blocked. Please allow pop-ups and try again.');
            printWindow.focus();
        },
        emailSaleOrderPdf: async (id: number, payload: EmailPdfRequest): Promise<EmailPdfResponse> => {
            const res = await fetch(`${BASE_URL}/sales/orders/${id}/email-pdf`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to send sale order PDF'));
            return res.json();
        },
        createOrderShareLink: async (id: number, payload?: EstimateShareLinkRequest): Promise<EstimateShareLinkResponse> => {
            const res = await fetch(`${BASE_URL}/sales/orders/${id}/share-link`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload || {}),
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to create sales order share link'));
            return res.json();
        },
        revokeOrderShareLink: async (id: number): Promise<void> => {
            const res = await fetch(`${BASE_URL}/sales/orders/${id}/share-link/revoke`, {
                method: 'POST',
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to revoke sales order share link'));
        },
        downloadDeliveryNote: async (id: number) => {
            const printUrl = `${BASE_URL}/delivery/${id}/print?autoprint=1`;
            const printWindow = window.open(printUrl, '_blank');
            if (!printWindow) throw new Error('Pop-up blocked. Please allow pop-ups and try again.');
            printWindow.focus();
        },
        emailDeliveryPdf: async (id: number, payload: EmailPdfRequest): Promise<EmailPdfResponse> => {
            const res = await fetch(`${BASE_URL}/delivery/${id}/email-pdf`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to send delivery note PDF'));
            return res.json();
        },
        createDeliveryShareLink: async (id: number, payload?: EstimateShareLinkRequest): Promise<EstimateShareLinkResponse> => {
            const res = await fetch(`${BASE_URL}/delivery/${id}/share-link`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload || {}),
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to create delivery note share link'));
            return res.json();
        },
        revokeDeliveryShareLink: async (id: number): Promise<void> => {
            const res = await fetch(`${BASE_URL}/delivery/${id}/share-link/revoke`, {
                method: 'POST',
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to revoke delivery note share link'));
        },
        listDeliveries: async (): Promise<DeliveryNoteSummary[]> => {
            const res = await fetch(`${BASE_URL}/delivery/`);
            if (!res.ok) throw new Error('Failed to fetch delivery notes');
            return res.json();
        },
        listDeliveriesPaged: async (filters?: {
            so_id?: number;
            q?: string;
            status_id?: number;
            date_from?: string;
            date_to?: string;
            limit?: number;
            offset?: number;
        }): Promise<PagedResult<DeliveryNoteSummary>> => {
            const params = new URLSearchParams();
            if (typeof filters?.so_id === 'number') params.set('so_id', String(filters.so_id));
            if (filters?.q) params.set('q', filters.q);
            if (typeof filters?.status_id === 'number') params.set('status_id', String(filters.status_id));
            if (filters?.date_from) params.set('date_from', filters.date_from);
            if (filters?.date_to) params.set('date_to', filters.date_to);
            if (typeof filters?.limit === 'number') params.set('limit', String(filters.limit));
            if (typeof filters?.offset === 'number') params.set('offset', String(filters.offset));
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await fetch(`${BASE_URL}/delivery/paged${suffix}`);
            if (!res.ok) throw new Error('Failed to fetch delivery notes');
            return res.json();
        },
        getDeliveriesForOrder: async (soId: number): Promise<DeliveryNoteSummary[]> => {
            const res = await fetch(`${BASE_URL}/delivery/so/${soId}`);
            if (!res.ok) throw new Error('Failed to fetch deliveries');
            return res.json();
        },
        getDetail: async (dnId: number): Promise<DeliveryNoteDetail> => {
            const res = await fetch(`${BASE_URL}/delivery/${dnId}`);
            if (!res.ok) throw new Error('Failed to fetch delivery note');
            return res.json();
        },
        getDeliveryDispatchPrecheck: async (dnId: number): Promise<DeliveryDispatchPrecheck> => {
            const res = await fetch(`${BASE_URL}/delivery/${dnId}/dispatch-precheck`);
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to fetch dispatch stock precheck'));
            return res.json();
        },
        createInvoiceFromDelivery: async (dnId: number): Promise<Invoice> => {
            const res = await fetch(`${BASE_URL}/delivery/${dnId}/create-invoice`, {
                method: 'POST',
            });
            if (!res.ok) {
                throw new Error(await getApiErrorMessage(res, 'Failed to create invoice from delivery note'));
            }
            return res.json();
        },
        previewUnbilledDeliveryInvoice: async (filters: {
            so_id: number;
            mode: 'spot' | 'cutoff';
            dn_id?: number;
            cutoff_date?: string;
        }): Promise<UnbilledDeliveryPreview> => {
            const params = new URLSearchParams({
                so_id: String(filters.so_id),
                mode: filters.mode,
            });
            if (typeof filters.dn_id === 'number') params.set('dn_id', String(filters.dn_id));
            if (filters.cutoff_date) params.set('cutoff_date', filters.cutoff_date);
            const res = await fetch(`${BASE_URL}/sales/invoices/unbilled-deliveries/preview?${params.toString()}`);
            if (!res.ok) {
                throw new Error(await getApiErrorMessage(res, 'Failed to preview unbilled deliveries'));
            }
            return res.json();
        },
        createInvoiceFromUnbilledDeliveries: async (payload: UnbilledDeliveryInvoiceCreateRequest): Promise<Invoice> => {
            const res = await fetch(`${BASE_URL}/sales/invoices/from-unbilled-deliveries`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                throw new Error(await getApiErrorMessage(res, 'Failed to create invoice from unbilled deliveries'));
            }
            return res.json();
        },
        createDelivery: async (payload: DeliveryCreateRequest): Promise<DeliveryNoteSummary> => {
            const res = await fetch(`${BASE_URL}/delivery/create`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({ detail: 'Failed to create delivery' }));
                throw new Error(err.detail || 'Failed to create delivery');
            }
            return res.json();
        },
        updateDelivery: async (id: number, payload: DeliveryUpdateRequest): Promise<DeliveryNoteSummary> => {
            const res = await fetch(`${BASE_URL}/delivery/${id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({ detail: 'Failed to update delivery' }));
                throw new Error(err.detail || 'Failed to update delivery');
            }
            return res.json();
        },
        deleteDelivery: async (id: number): Promise<void> => {
            const res = await fetch(`${BASE_URL}/delivery/${id}`, {
                method: 'DELETE'
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({ detail: 'Failed to delete delivery' }));
                throw new Error(err.detail || 'Failed to delete delivery');
            }
        },
        dispatchDelivery: async (id: number): Promise<any> => {
            const res = await fetch(`${BASE_URL}/delivery/${id}/dispatch`, {
                method: 'POST'
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to dispatch delivery note');
            }
            return res.json();
        },
        cancelDelivery: async (id: number): Promise<any> => {
            const res = await fetch(`${BASE_URL}/delivery/${id}/cancel`, {
                method: 'POST'
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to cancel delivery note');
            }
            return res.json();
        },
        undoDispatchDelivery: async (id: number): Promise<{ undispatch_warning?: string[]; [key: string]: any }> => {
            const res = await fetch(`${BASE_URL}/delivery/${id}/undispatch`, {
                method: 'POST'
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to undo dispatch delivery note');
            }
            return res.json();
        },
        listInvoices: async (): Promise<Invoice[]> => {
            const res = await fetch(`${BASE_URL}/sales/invoices`);
            if (!res.ok) throw new Error('Failed to fetch invoices');
            return res.json();
        },
        listInvoicesPaged: async (filters?: {
            q?: string;
            invoice_type?: string;
            payment_status?: string;
            estimate_id?: number;
            date_from?: string;
            date_to?: string;
            limit?: number;
            offset?: number;
        }): Promise<PagedResult<Invoice>> => {
            const params = new URLSearchParams();
            if (filters?.q) params.set('q', filters.q);
            if (filters?.invoice_type) params.set('invoice_type', filters.invoice_type);
            if (filters?.payment_status) params.set('payment_status', filters.payment_status);
            if (typeof filters?.estimate_id === 'number') params.set('estimate_id', String(filters.estimate_id));
            if (filters?.date_from) params.set('date_from', filters.date_from);
            if (filters?.date_to) params.set('date_to', filters.date_to);
            if (typeof filters?.limit === 'number') params.set('limit', String(filters.limit));
            if (typeof filters?.offset === 'number') params.set('offset', String(filters.offset));
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await fetch(`${BASE_URL}/sales/invoices/paged${suffix}`);
            if (!res.ok) throw new Error('Failed to fetch invoices');
            return res.json();
        },
        listInvoiceSourceOptions: async (filters: InvoiceSourceFilter = {}): Promise<InvoiceSourceOption[]> => {
            const params = new URLSearchParams();
            if (filters.query) params.set('query', filters.query);
            if (typeof filters.customer_id === 'number') params.set('customer_id', String(filters.customer_id));
            if (filters.so_status_ids?.length) params.set('so_status_ids', filters.so_status_ids.join(','));
            if (filters.wo_status_ids?.length) params.set('wo_status_ids', filters.wo_status_ids.join(','));
            if (typeof filters.limit === 'number') params.set('limit', String(filters.limit));
            if (typeof filters.offset === 'number') params.set('offset', String(filters.offset));
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await fetch(`${BASE_URL}/sales/invoice-sources/options${suffix}`);
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to fetch invoice source options'));
            return res.json();
        },
        createInvoice: async (payload: {
            estimate_id: number,
            so_id?: number,
            dn_id?: number,
            wo_id?: number,
            invoice_type?: 'TAX' | 'PROFORMA',
            customer_po_no?: string,
            invoice_date?: string,
            invoice_notes?: string,
            items?: { estimate_part_id: number; quantity: number }[]
        }): Promise<Invoice> => {
            const res = await fetch(`${BASE_URL}/sales/invoices`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) {
                const fallback = 'Failed to create invoice';
                const data = await res.json().catch(() => null);
                const parsed = extractApiErrorPayload(data, fallback);
                const err = new Error(parsed.message) as LegacyApiError;
                err.status = res.status;
                if (parsed.errorCode) err.error_code = parsed.errorCode;
                if (parsed.details) err.details = parsed.details;
                if (data !== null) err.data = data;
                throw err;
            }
            return res.json();
        },
        convertInvoiceToTax: async (id: number): Promise<{ invoice_id: number; invoice_type: 'TAX'; status: string }> => {
            const res = await fetch(`${BASE_URL}/sales/invoices/${id}/convert-to-tax`, {
                method: 'POST',
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to convert invoice to tax'));
            return res.json();
        },
        transitionInvoice: async (id: number, action: string, reason?: string): Promise<any> => {
            const res = await fetch(`${BASE_URL}/sales/invoices/${id}/transition`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action, reason }),
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to transition invoice'));
            return res.json();
        },
        downloadInvoicePDF: async (id: number) => {
            const printUrl = `${BASE_URL}/sales/invoices/${id}/print?autoprint=1`;
            const printWindow = window.open(printUrl, '_blank');
            if (!printWindow) {
                throw new Error('Pop-up blocked. Please allow pop-ups and try again.');
            }
            printWindow.focus();
        },
        emailInvoicePdf: async (id: number, payload: EmailPdfRequest): Promise<EmailPdfResponse> => {
            const res = await fetch(`${BASE_URL}/sales/invoices/${id}/email-pdf`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to send invoice PDF'));
            return res.json();
        },
        createInvoiceShareLink: async (id: number, payload?: EstimateShareLinkRequest): Promise<EstimateShareLinkResponse> => {
            const res = await fetch(`${BASE_URL}/sales/invoices/${id}/share-link`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload || {}),
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to create invoice share link'));
            return res.json();
        },
        revokeInvoiceShareLink: async (id: number): Promise<void> => {
            const res = await fetch(`${BASE_URL}/sales/invoices/${id}/share-link/revoke`, {
                method: 'POST',
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to revoke invoice share link'));
        },
        getInvoice: async (id: number): Promise<Invoice> => {
            const res = await fetch(`${BASE_URL}/sales/invoices/${id}`);
            if (!res.ok) throw new Error('Failed to fetch invoice details');
            return res.json();
        },
        listInvoiceDocuments: async (id: number, category: DocumentCategory = 'ACK_INVOICE'): Promise<DocumentAttachment[]> => {
            const res = await fetch(`${BASE_URL}/sales/invoices/${id}/documents?category=${encodeURIComponent(category)}`);
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to fetch invoice documents'));
            const data: DocumentAttachmentListResponse = await res.json();
            return Array.isArray(data?.documents) ? data.documents : [];
        },
        uploadInvoiceDocument: async (
            id: number,
            payload: { file: File; category?: DocumentCategory; note?: string; mark_current?: boolean }
        ): Promise<DocumentAttachment> => {
            const formData = new FormData();
            formData.append('file', payload.file);
            formData.append('category', payload.category || 'ACK_INVOICE');
            if (payload.note !== undefined) formData.append('note', payload.note);
            formData.append('mark_current', payload.mark_current === false ? 'false' : 'true');
            const res = await fetch(`${BASE_URL}/sales/invoices/${id}/documents`, {
                method: 'POST',
                body: formData,
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to upload invoice document'));
            return res.json();
        },
        setInvoiceDocumentCurrent: async (id: number, docId: number): Promise<DocumentAttachment> => {
            const res = await fetch(`${BASE_URL}/sales/invoices/${id}/documents/${docId}/set-current`, {
                method: 'PATCH',
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to set current document'));
            return res.json();
        },
        deleteInvoiceDocument: async (id: number, docId: number): Promise<void> => {
            const res = await fetch(`${BASE_URL}/sales/invoices/${id}/documents/${docId}`, {
                method: 'DELETE',
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to delete document'));
        },
        downloadInvoiceDocument: async (id: number, docId: number): Promise<void> => {
            const url = `${BASE_URL}/sales/invoices/${id}/documents/${docId}/download`;
            const popup = window.open(url, '_blank');
            if (!popup) throw new Error('Pop-up blocked. Please allow pop-ups and try again.');
            popup.focus();
        },
        previewInvoiceDocumentUrl: (id: number, docId: number): string => `${BASE_URL}/sales/invoices/${id}/documents/${docId}/preview`,
        listCreditNotes: async (): Promise<CreditNote[]> => {
            const res = await fetch(`${BASE_URL}/sales/credit-notes`);
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to fetch credit notes'));
            return res.json();
        },
        getCreditNote: async (id: number): Promise<CreditNote> => {
            const res = await fetch(`${BASE_URL}/sales/credit-notes/${id}`);
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to fetch credit note'));
            return res.json();
        },
        createCreditNote: async (payload: {
            source_invoice_id: number;
            mode?: CreditNoteMode;
            items?: { invoice_detail_id: number; quantity: number }[];
            adjustment_amount?: number;
            vat_percent?: number;
            reason?: string;
            remarks?: string;
            date?: string;
        }): Promise<CreditNote> => {
            const res = await fetch(`${BASE_URL}/sales/credit-notes`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to create credit note'));
            return res.json();
        },
        transitionCreditNote: async (id: number, action: 'post' | 'cancel', reason?: string): Promise<any> => {
            const res = await fetch(`${BASE_URL}/sales/credit-notes/${id}/transition`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action, reason }),
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to update credit note'));
            return res.json();
        },
        downloadCreditNotePDF: async (id: number): Promise<void> => {
            const printUrl = `${BASE_URL}/sales/credit-notes/${id}/print`;
            const printWindow = window.open(printUrl, '_blank');
            if (!printWindow) throw new Error('Pop-up blocked. Please allow pop-ups and try again.');
            printWindow.focus();
        },
        emailCreditNotePdf: async (id: number, payload: EmailPdfRequest): Promise<EmailPdfResponse> => {
            const res = await fetch(`${BASE_URL}/sales/credit-notes/${id}/email-pdf`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to send credit note PDF'));
            return res.json();
        },
        createCreditNoteShareLink: async (id: number, payload?: EstimateShareLinkRequest): Promise<EstimateShareLinkResponse> => {
            const res = await fetch(`${BASE_URL}/sales/credit-notes/${id}/share-link`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload || {}),
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to create credit note share link'));
            return res.json();
        },
        revokeCreditNoteShareLink: async (id: number): Promise<void> => {
            const res = await fetch(`${BASE_URL}/sales/credit-notes/${id}/share-link/revoke`, {
                method: 'POST',
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to revoke credit note share link'));
        },
        deleteInvoice: async (id: number): Promise<void> => {
            const res = await fetch(`${BASE_URL}/sales/invoices/${id}`, {
                method: 'DELETE'
            });
            if (!res.ok) {
                let detail = '';
                try {
                    const errData = await res.json();
                    detail = errData?.detail || '';
                } catch {
                    detail = '';
                }
                if (!detail && res.status === 405) {
                    detail = 'Delete endpoint is not available on this server version. Deploy latest backend.';
                }
                throw new Error(detail || 'Failed to delete invoice');
            }
        },
        updateInvoice: async (id: number, payload: {
            estimate_id: number,
            customer_po_no?: string,
            invoice_date?: string,
            invoice_notes?: string,
            items?: { estimate_part_id: number; quantity: number }[]
        }): Promise<Invoice> => {
            const res = await fetch(`${BASE_URL}/sales/invoices/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({ detail: 'Failed to update invoice' }));
                throw new Error(err.detail || 'Failed to update invoice');
            }
            return res.json();
        },
        createReceipt: async (payload: { customer_id: number; amount: number; receipt_date?: string; reference_no?: string; so_id?: number }) => {
            const res = await fetch(`${BASE_URL}/sales/receipts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) throw new Error('Failed to post receipt');
            return res.json();
        }
    },
    productionLogs: {
        create: async (payload: { wo_id: number; log_date: string; staff_name: string; hours_worked: number; description?: string }): Promise<any> => {
            const res = await fetch(`${BASE_URL}/production-logs/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) throw new Error('Failed to create production log');
            return res.json();
        },
        getByWorkOrder: async (woId: number): Promise<any[]> => {
            const res = await fetch(`${BASE_URL}/production-logs/wo/${woId}`);
            if (!res.ok) throw new Error('Failed to fetch production logs');
            return res.json();
        }
    },
    purchasing: {
        listBills: async (): Promise<SupplierBill[]> => {
            const res = await fetch(`${BASE_URL}/purchasing/bills`);
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to fetch supplier bills'));
            return res.json();
        },
        listBillsPaged: async (filters?: {
            q?: string;
            status_id?: number;
            ageing?: string;
            limit?: number;
            offset?: number;
        }): Promise<PagedResult<SupplierBill>> => {
            const params = new URLSearchParams();
            if (filters?.q) params.set('q', filters.q);
            if (typeof filters?.status_id === 'number') params.set('status_id', String(filters.status_id));
            if (filters?.ageing) params.set('ageing', filters.ageing);
            if (typeof filters?.limit === 'number') params.set('limit', String(filters.limit));
            if (typeof filters?.offset === 'number') params.set('offset', String(filters.offset));
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await fetch(`${BASE_URL}/purchasing/bills/paged${suffix}`);
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to fetch supplier bills'));
            return res.json();
        },
        getBill: async (id: number): Promise<SupplierBill> => {
            const res = await fetch(`${BASE_URL}/purchasing/bills/${id}`);
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to fetch supplier bill'));
            return res.json();
        },
        createBill: async (payload: { grn_id: number; bill_date?: string; reference_no?: string; remarks?: string; discount_amount?: number; vat_percent?: number }): Promise<SupplierBill> => {
            const res = await fetch(`${BASE_URL}/purchasing/bills`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to create supplier bill'));
            return res.json();
        },
        updateBill: async (id: number, payload: SupplierBillUpdatePayload): Promise<SupplierBill> => {
            const res = await fetch(`${BASE_URL}/purchasing/bills/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to update supplier bill'));
            return res.json();
        },
        transitionBill: async (id: number, action: string, reason?: string): Promise<any> => {
            const res = await fetch(`${BASE_URL}/purchasing/bills/${id}/transition`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action, reason })
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to change supplier bill status'));
            return res.json();
        },
        reviewBillVat: async (id: number, payload: { recovery_percent: number; vat_treatment: string; nonrecoverable_vat_account_id?: number | null }): Promise<any> => {
            const res = await fetch(`${BASE_URL}/purchasing/bills/${id}/vat-recovery`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to review Input VAT recovery'));
            return res.json();
        },
        listOrders: async (): Promise<PurchaseOrder[]> => {
            const res = await fetch(`${BASE_URL}/purchasing/orders`);
            if (!res.ok) throw new Error('Failed to fetch purchase orders');
            return res.json();
        },
        listOrdersPaged: async (filters?: {
            q?: string;
            status_id?: number;
            limit?: number;
            offset?: number;
        }): Promise<PagedResult<PurchaseOrder>> => {
            const params = new URLSearchParams();
            if (filters?.q) params.set('q', filters.q);
            if (typeof filters?.status_id === 'number') params.set('status_id', String(filters.status_id));
            if (typeof filters?.limit === 'number') params.set('limit', String(filters.limit));
            if (typeof filters?.offset === 'number') params.set('offset', String(filters.offset));
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await fetch(`${BASE_URL}/purchasing/orders/paged${suffix}`);
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to fetch purchase orders'));
            return res.json();
        },
        createPurchaseOrder: async (payload: any): Promise<PurchaseOrder> => {
            const res = await fetch(`${BASE_URL}/purchasing/orders`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) throw new Error('Failed to create purchase order');
            return res.json();
        },
        updatePurchaseOrder: async (id: number, payload: any): Promise<PurchaseOrder> => {
            const res = await fetch(`${BASE_URL}/purchasing/orders/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) throw new Error('Failed to update purchase order');
            return res.json();
        },
        transitionPurchaseOrder: async (id: number, action: string, reason?: string): Promise<any> => {
            const res = await fetch(`${BASE_URL}/purchasing/orders/${id}/transition`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action, reason })
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.detail || 'Failed to change purchase order status');
            return data;
        },
        deletePurchaseOrder: async (id: number): Promise<void> => {
            const res = await fetch(`${BASE_URL}/purchasing/orders/${id}`, { method: 'DELETE' });
            if (!res.ok) throw new Error('Failed to delete purchase order');
        },
        getOrderDetail: async (id: number): Promise<PurchaseOrder> => {
            const res = await fetch(`${BASE_URL}/purchasing/orders/${id}`);
            if (!res.ok) throw new Error('Failed to fetch purchase order details');
            return res.json();
        },
        getOrderOverview: async (id: number): Promise<PurchaseOrderOverview> => {
            const res = await fetch(`${BASE_URL}/purchasing/orders/${id}/overview`);
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to fetch purchase order overview'));
            return res.json();
        },
        listGRNs: async (): Promise<GRN[]> => {
            const res = await fetch(`${BASE_URL}/purchasing/grn`);
            if (!res.ok) throw new Error('Failed to fetch GRNs');
            return res.json();
        },
        listGRNsPaged: async (filters?: {
            q?: string;
            source?: string;
            bill_status?: string;
            limit?: number;
            offset?: number;
        }): Promise<PagedResult<GRN>> => {
            const params = new URLSearchParams();
            if (filters?.q) params.set('q', filters.q);
            if (filters?.source) params.set('source', filters.source);
            if (filters?.bill_status) params.set('bill_status', filters.bill_status);
            if (typeof filters?.limit === 'number') params.set('limit', String(filters.limit));
            if (typeof filters?.offset === 'number') params.set('offset', String(filters.offset));
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await fetch(`${BASE_URL}/purchasing/grn/paged${suffix}`);
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to fetch GRNs'));
            return res.json();
        },
        getGRNDetail: async (id: number): Promise<GRN & { details: GRNDetail[] }> => {
            const res = await fetch(`${BASE_URL}/purchasing/grn/${id}`);
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to fetch GRN details'));
            return res.json();
        },
        getGRNOverview: async (id: number): Promise<GrnOverview> => {
            const res = await fetch(`${BASE_URL}/purchasing/grn/${id}/overview`);
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to fetch GRN overview'));
            return res.json();
        },
        getPurchaseOrder: async (id: number): Promise<PurchaseOrder> => {
            const res = await fetch(`${BASE_URL}/purchasing/orders/${id}`);
            if (!res.ok) throw new Error('Failed to fetch purchase order');
            return res.json();
        },
        createGRN: async (payload: any): Promise<any> => {
            const res = await fetch(`${BASE_URL}/purchasing/grn`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to create GRN'));
            return res.json();
        },
        updateGRN: async (id: number, payload: any): Promise<any> => {
            const res = await fetch(`${BASE_URL}/purchasing/grn/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to update GRN'));
            return res.json();
        },
        transitionGRN: async (id: number, payload: { action: string; reason?: string }): Promise<any> => {
            const res = await fetch(`${BASE_URL}/purchasing/grn/${id}/transition`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to transition GRN'));
            return res.json();
        },
        deleteGRN: async (id: number): Promise<void> => {
            const res = await fetch(`${BASE_URL}/purchasing/grn/${id}`, { method: 'DELETE' });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to delete GRN'));
        },
        createPayment: async (payload: { supplier_id: number; amount: number; payment_date?: string; reference_no?: string }) => {
            const res = await fetch(`${BASE_URL}/purchasing/payments`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) throw new Error('Failed to post payment');
            return res.json();
        },
        convertFromEstimate: async (estimateId: number, supplierId: number): Promise<PurchaseOrder> => {
            const res = await fetch(`${BASE_URL}/purchasing/orders/convert/estimate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ estimate_id: estimateId, supplier_id: supplierId })
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.detail || 'Conversion failed');
            }
            return res.json();
        },
        downloadPDF: async (id: number) => {
            const printUrl = `${BASE_URL}/purchasing/orders/${id}/print?autoprint=1`;
            const printWindow = window.open(printUrl, '_blank');
            if (!printWindow) throw new Error('Pop-up blocked. Please allow pop-ups and try again.');
            printWindow.focus();
        },
        emailPdf: async (id: number, payload: EmailPdfRequest): Promise<EmailPdfResponse> => {
            const res = await fetch(`${BASE_URL}/purchasing/orders/${id}/email-pdf`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to send purchase order PDF'));
            return res.json();
        },
        createShareLink: async (id: number, payload?: EstimateShareLinkRequest): Promise<EstimateShareLinkResponse> => {
            const res = await fetch(`${BASE_URL}/purchasing/orders/${id}/share-link`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload || {}),
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to create purchase order share link'));
            return res.json();
        },
        revokeShareLink: async (id: number): Promise<void> => {
            const res = await fetch(`${BASE_URL}/purchasing/orders/${id}/share-link/revoke`, {
                method: 'POST',
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to revoke purchase order share link'));
        },
    },
    materials: {
        list: async (): Promise<Material[]> => {
            const res = await fetch(`${BASE_URL}/legacy/materials`);
            if (!res.ok) throw new Error('Failed to fetch materials');
            return res.json();
        },
        listGrp: async (): Promise<GRPMaterialCatalogItem[]> => {
            const res = await fetch(`${BASE_URL}/materials/grp`);
            if (!res.ok) throw new Error('Failed to fetch GRP materials');
            return res.json();
        }
    },
    bom: {
        listCatalog: async (): Promise<BOMCatalogItem[]> => {
            const res = await fetch(`${BASE_URL}/estimates/bom-materials`);
            if (!res.ok) throw new Error('Failed to fetch BOM materials');
            return res.json();
        },
        updatePart: async (
            partId: number,
            items: { bom_material_id?: number; material_id?: number; quantity: number; unit_rate: number; }[],
        ) => {
            const res = await fetch(`${BASE_URL}/estimates/parts/${partId}/bom`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items })
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.detail || 'Failed to save BOM items');
            }
            return res.json();
        }
    },
    masterData: {
        list: fetchMasterData,
        listMaterialTypes: async (): Promise<MaterialType[]> => {
            const materialTypes = await fetchMasterData('tbl_material_types');
            return ensureGrgMaterialTypes(materialTypes);
        },
        listUnits: async (): Promise<Unit[]> => {
            return fetchMasterData('tbl_units');
        },
        listMouldTypes: async (): Promise<MouldType[]> => {
            return fetchMasterData('tbl_mould_types');
        }
    },
    getDesignMixModels: async (): Promise<DesignMixModel[]> => {
        const res = await fetch(`${BASE_URL}/legacy/design-mix-models`);
        if (!res.ok) throw new Error('Failed to fetch design mix models');
        return res.json();
    },
    getGRCTypes: async (): Promise<GRCType[]> => {
        const res = await fetch(`${BASE_URL}/legacy/grc-types`);
        if (!res.ok) throw new Error('Failed to fetch GRC types');
        return res.json();
    },
    getProjectTypes: async (): Promise<ProjectType[]> => {
        const res = await fetch(`${BASE_URL}/legacy/project-types`);
        if (!res.ok) throw new Error('Failed to fetch project types');
        return res.json();
    },

    inquiries: {
        list: async (): Promise<InquirySummary[]> => {
            const res = await fetch(`${BASE_URL}/inquiries`);
            if (!res.ok) throw new Error('Failed to fetch inquiries');
            return res.json();
        },
        listPaged: async (filters?: {
            q?: string;
            status_id?: number;
            date_from?: string;
            date_to?: string;
            limit?: number;
            offset?: number;
        }): Promise<PagedResult<InquirySummary>> => {
            const params = new URLSearchParams();
            if (filters?.q) params.set('q', filters.q);
            if (typeof filters?.status_id === 'number') params.set('status_id', String(filters.status_id));
            if (filters?.date_from) params.set('date_from', filters.date_from);
            if (filters?.date_to) params.set('date_to', filters.date_to);
            if (typeof filters?.limit === 'number') params.set('limit', String(filters.limit));
            if (typeof filters?.offset === 'number') params.set('offset', String(filters.offset));
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await fetch(`${BASE_URL}/inquiries/paged${suffix}`);
            if (!res.ok) throw new Error('Failed to fetch inquiries');
            return res.json();
        },
        getDetail: async (id: number): Promise<InquiryDetail> => {
            const res = await fetch(`${BASE_URL}/inquiries/${id}`);
            if (!res.ok) throw new Error('Failed to fetch inquiry detail');
            return res.json();
        },
        listDocuments: async (id: number, category: DocumentCategory = 'ENQUIRY_CONTENT'): Promise<DocumentAttachment[]> => {
            const res = await fetch(`${BASE_URL}/inquiries/${id}/documents?category=${encodeURIComponent(category)}`);
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to fetch inquiry documents'));
            const data: DocumentAttachmentListResponse = await res.json();
            return Array.isArray(data?.documents) ? data.documents : [];
        },
        uploadDocument: async (
            id: number,
            payload: { file: File; category?: DocumentCategory; note?: string; mark_current?: boolean }
        ): Promise<DocumentAttachment> => {
            const formData = new FormData();
            formData.append('file', payload.file);
            formData.append('category', payload.category || 'ENQUIRY_CONTENT');
            if (payload.note !== undefined) formData.append('note', payload.note);
            formData.append('mark_current', payload.mark_current === false ? 'false' : 'true');
            const res = await fetch(`${BASE_URL}/inquiries/${id}/documents`, {
                method: 'POST',
                body: formData,
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to upload inquiry document'));
            return res.json();
        },
        setDocumentCurrent: async (id: number, docId: number): Promise<DocumentAttachment> => {
            const res = await fetch(`${BASE_URL}/inquiries/${id}/documents/${docId}/set-current`, {
                method: 'PATCH',
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to set current document'));
            return res.json();
        },
        deleteDocument: async (id: number, docId: number): Promise<void> => {
            const res = await fetch(`${BASE_URL}/inquiries/${id}/documents/${docId}`, {
                method: 'DELETE',
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to delete document'));
        },
        downloadDocument: async (id: number, docId: number): Promise<void> => {
            const url = `${BASE_URL}/inquiries/${id}/documents/${docId}/download`;
            const popup = window.open(url, '_blank');
            if (!popup) throw new Error('Pop-up blocked. Please allow pop-ups and try again.');
            popup.focus();
        },
        previewDocumentUrl: (id: number, docId: number): string => `${BASE_URL}/inquiries/${id}/documents/${docId}/preview`,
        convert: async (id: number, reason?: string): Promise<any> => {
            const res = await fetch(`${BASE_URL}/inquiries/${id}/convert`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ reason })
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to convert inquiry'));
            return res.json();
        },
        transition: async (id: number, action: string, reason?: string): Promise<any> => {
            const res = await fetch(`${BASE_URL}/inquiries/${id}/transition`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action, reason })
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to change inquiry status'));
            return res.json();
        },
        update: async (id: number, payload: any): Promise<any> => {
            const res = await fetch(`${BASE_URL}/inquiries/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) throw new Error('Failed to update inquiry');
            return res.json();
        },
        create: async (payload: any): Promise<any> => {
            const res = await fetch(`${BASE_URL}/inquiries`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) throw new Error('Failed to create inquiry');
            return res.json();
        },
        delete: async (id: number): Promise<void> => {
            const res = await fetch(`${BASE_URL}/inquiries/${id}`, { method: 'DELETE' });
            if (!res.ok) throw new Error('Failed to delete inquiry');
        }
    },
    reports: {
        getStockValuation: async (): Promise<any[]> => {
            const res = await fetch(`${BASE_URL}/reports/stock-valuation`);
            if (!res.ok) throw new Error('Failed to fetch stock valuation');
            return res.json();
        },
        getJobProfitability: async (): Promise<any[]> => {
            const res = await fetch(`${BASE_URL}/reports/job-profitability`);
            if (!res.ok) throw new Error('Failed to fetch job profitability');
            return res.json();
        },
        getJobProfitabilityPaged: async (filters?: { q?: string; limit?: number; offset?: number }): Promise<PagedResult<any>> => {
            const params = new URLSearchParams();
            if (filters?.q) params.set('q', filters.q);
            if (typeof filters?.limit === 'number') params.set('limit', String(filters.limit));
            if (typeof filters?.offset === 'number') params.set('offset', String(filters.offset));
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await fetch(`${BASE_URL}/reports/job-profitability/paged${suffix}`);
            if (!res.ok) throw new Error('Failed to fetch paged job profitability');
            return res.json();
        },
        getVATSummary: async (): Promise<any> => {
            const res = await fetch(`${BASE_URL}/reports/vat-summary`);
            if (!res.ok) throw new Error('Failed to fetch VAT summary');
            return res.json();
        },
        exportJournals: async () => {
            const response = await fetch(`${BASE_URL}/reports/export/journals`);
            if (!response.ok) throw new Error('Failed to download journals');
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Journal_Export_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
        },
        getLifecycleReport: async (): Promise<any[]> => {
            const res = await fetch(`${BASE_URL}/reports/lifecycle`);
            if (!res.ok) throw new Error('Failed to fetch lifecycle report');
            return res.json();
        },
        getLifecycleReportPaged: async (filters?: { q?: string; limit?: number; offset?: number }): Promise<PagedResult<any>> => {
            const params = new URLSearchParams();
            if (filters?.q) params.set('q', filters.q);
            if (typeof filters?.limit === 'number') params.set('limit', String(filters.limit));
            if (typeof filters?.offset === 'number') params.set('offset', String(filters.offset));
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await fetch(`${BASE_URL}/reports/lifecycle/paged${suffix}`);
            if (!res.ok) throw new Error('Failed to fetch paged lifecycle report');
            return res.json();
        },
        getActiveJobsCount: async (): Promise<{ count: number }> => {
            const res = await fetch(`${BASE_URL}/reports/active-jobs-count`);
            if (!res.ok) throw new Error('Failed to fetch active jobs count');
            return res.json();
        }
    },
    system: {
        getDefaults: async (): Promise<any[]> => {
            const res = await fetch(`${BASE_URL}/system/settings/defaults`);
            if (!res.ok) throw new Error('Failed to fetch system defaults');
            const data = await res.json();
            if (Array.isArray(data) && data.length > 0) return data;
            return [
                { setting_name: 'Validity', setting_value: '15 Days from the date of Quotation' },
                {
                    setting_name: 'Annexure A', setting_value:
                        'Standard Technical Annexure - GRP / FRP Marine Boxes\n' +
                        'Description / Specification\n' +
                        'Product: GRP / FRP Marine Box with Lid\n' +
                        'Gelcoat Type: ISO Grade Gelcoat\n' +
                        'Gelcoat Color: RAL3020\n' +
                        'Resin Used: General Purpose (GP) Polyester Resin\n' +
                        'Body Thickness: Sides & Lid: 4 mm, Bottom: 5 mm\n' +
                        'Accessories Included:\n' +
                        '- SS 304 Hinges - 2 Nos / Box\n' +
                        '- SS 304 Toggle Latches - 2 Nos / Box\n' +
                        '- Rubber Gasket - 1 Set / Box'
                },
                {
                    setting_name: 'Annexure B', setting_value: ''
                },
            ];
        },
        getEmailConfig: async (): Promise<SMTPEmailConfig> => {
            const res = await fetch(`${BASE_URL}/settings/email/config`);
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to fetch email configuration'));
            return res.json();
        },
        updateEmailConfig: async (payload: Omit<SMTPEmailConfig, 'configured'>): Promise<SMTPEmailConfig> => {
            const res = await fetch(`${BASE_URL}/settings/email/config`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to save email configuration'));
            return res.json();
        },
        testEmailSend: async (payload: EmailPdfRequest): Promise<{ ok?: boolean; message?: string }> => {
            const res = await fetch(`${BASE_URL}/settings/email/test-send`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to send test email'));
            return res.json();
        },
    }
};

export interface DesignMixModel {
    id: number;
    name: string;
}

export interface GRCType {
    id: number;
    name: string;
}

export interface ProjectType {
    id: number;
    name: string;
}
